/**
 * TourManager: React context/provider for onboarding tours
 * Features:
 * - Lazy-loads react-joyride
 * - LocalStorage persistence
 * - MutationObserver for target detection
 * - WCAG 2.1 AA accessibility
 * - Non-modal coach-marks
 * - Sample data upload integration
 * - Post-save CTA sequential highlighting
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import type { CallBackProps, Step, Styles } from 'react-joyride';
import { onboardingSteps, postSaveCTASteps, type TourStep } from './tours/onboarding';
import { trackOnboardingEvent } from '@/utils/telemetry';
import { useProjectsStore } from '@/store/projects';
import { authorizedFetch } from '@/lib/queryClient';
import { apiRequest } from '@/lib/queryClient';

// Lazy-load react-joyride to keep bundle impact low
const Joyride = lazy(() => import('react-joyride').catch(err => {
  console.error('[TourManager] Failed to load react-joyride', err);
  throw err;
}));

type TourStatus = 'new' | 'in_progress' | 'completed' | 'dismissed';
type TourId = 'onboarding';

interface TourState {
  status: TourStatus;
  step: number;
  tourId: TourId | null;
  snoozeUntil?: number; // Timestamp for 7-day snooze
}

type OnboardingStatus = 'loading' | 'new' | 'completed' | 'dismissed';

interface TourContextValue {
  startTour: (id: TourId, force?: boolean) => void;
  resumeIfNeeded: () => void;
  dismiss: () => void;
  isActive: boolean;
  currentStep: number | null;
  skip: () => void;
  onboardingStatus: OnboardingStatus;
}

const TourContext = createContext<TourContextValue | null>(null);

const STORAGE_KEY_STATUS = 'onboarding.v1.status';
const STORAGE_KEY_STEP = 'onboarding.v1.step';
const STORAGE_KEY_SNOOZE = 'onboarding.v1.snooze_until';
const SNOOZE_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Check if tour is forced via environment variable or localStorage
 * Used for testing - allows tour to always appear when forced
 */
function isTourForced(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    // Check environment variable (for build-time override)
    if ((import.meta as any).env?.VITE_LUMI_TOUR_FORCE === '1') return true;
    // Check localStorage (for runtime override)
    if (localStorage.getItem('lumi:force-tour') === '1') return true;
    return false;
  } catch {
    return false;
  }
}

/**
 * Wait for a selector to appear in the DOM
 * @param selector CSS selector string
 * @param options Timeout and interval options
 * @returns Promise that resolves when element is found, or rejects on timeout
 */
function waitForSelector(
  selector: string,
  options: { timeoutMs?: number; intervalMs?: number } = {}
): Promise<HTMLElement> {
  const { timeoutMs = 10000, intervalMs = 200 } = options;
  
  return new Promise((resolve, reject) => {
    // Check immediately
    const element = document.querySelector(selector) as HTMLElement;
    if (element) {
      resolve(element);
      return;
    }

    // Use MutationObserver for efficient watching
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector) as HTMLElement;
      if (el) {
        observer.disconnect();
        resolve(el);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Timeout fallback
    const timeout = setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Timeout waiting for selector: ${selector}`));
    }, timeoutMs);

    // Also check periodically as fallback (in case MutationObserver misses something)
    const interval = setInterval(() => {
      const el = document.querySelector(selector) as HTMLElement;
      if (el) {
        clearInterval(interval);
        clearTimeout(timeout);
        observer.disconnect();
        resolve(el);
      }
    }, intervalMs);
  });
}

function loadTourState(): TourState {
  try {
    if (typeof window === 'undefined') {
      return { status: 'new', step: 0, tourId: null };
    }
    
    const status = (localStorage.getItem(STORAGE_KEY_STATUS) || 'new') as TourStatus;
    const rawStep = parseInt(localStorage.getItem(STORAGE_KEY_STEP) || '0', 10);
    const step = Number.isFinite(rawStep) ? rawStep : 0;
    const snoozeUntilStr = localStorage.getItem(STORAGE_KEY_SNOOZE);
    const snoozeUntil = snoozeUntilStr ? parseInt(snoozeUntilStr, 10) : undefined;

    const maxStep = onboardingSteps.length - 1;

    // Validate step range only for active/completed tours
    // This auto-heals corrupted data by resetting to 'new' state
    if (
      (status === 'in_progress' || status === 'completed') &&
      (step < 0 || step > maxStep)
    ) {
      console.warn(
        '[TourManager] Invalid stored tour step',
        step,
        'for status',
        status,
        '- resetting to new'
      );
      // Directly write to localStorage to fix corrupted data
      localStorage.setItem(STORAGE_KEY_STATUS, 'new');
      localStorage.setItem(STORAGE_KEY_STEP, '0');
      localStorage.removeItem(STORAGE_KEY_SNOOZE);
      return { status: 'new', step: 0, tourId: null };
    }

    // If dismissed and snooze has expired, reset to 'new' to allow auto-start again
    if (status === 'dismissed' && snoozeUntil) {
      if (Date.now() >= snoozeUntil) {
        // Snooze expired - reset to 'new' state
        const reset: TourState = { status: 'new', step: 0, tourId: null };
        try {
          saveTourState(reset);
        } catch {}
        return reset;
      }
      // Still within snooze period
      return { status: 'dismissed', step, tourId: null, snoozeUntil };
    }

    // Normal case
    return {
      status,
      step,
      tourId: status === 'in_progress' || status === 'completed' ? 'onboarding' : null,
      snoozeUntil,
    };
  } catch (err) {
    console.error('[TourManager] Failed to load tour state, resetting', err);
    const reset: TourState = { status: 'new', step: 0, tourId: null };
    try {
      saveTourState(reset);
    } catch {}
    return reset;
  }
}

function saveTourState(state: TourState): void {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY_STATUS, state.status);
    localStorage.setItem(STORAGE_KEY_STEP, String(state.step));
    if (state.snoozeUntil) {
      localStorage.setItem(STORAGE_KEY_SNOOZE, String(state.snoozeUntil));
    } else {
      localStorage.removeItem(STORAGE_KEY_SNOOZE);
    }
  } catch (err) {
    console.error('[TourManager] Failed to save tour state', err);
  }
}

/**
 * Generate sample CSV data for onboarding
 * Structure: X-axis = site, Y-axis = value, Color = treatment
 */
function generateSampleCSV(): string {
  const headers = 'site,treatment,value\n';
  const rows: string[] = [];
  const sites = ['Site A', 'Site B', 'Site C', 'Site D'];
  const treatments = ['Control', 'Treatment'];
  
  // Base means for each site (Control values)
  // All sites have similar baseline values - no sequential increase
  // Focus is on showing Treatment > Control across all sites
  const siteMeans: Record<string, number> = {
    'Site A': 15.2,
    'Site B': 14.8,
    'Site C': 15.5,
    'Site D': 14.9,
  };
  
  // Treatment effect: Treatment is consistently ~3.5 units higher than Control
  const treatmentEffect = 3.5;
  
  // Standard deviation for realistic variation (smaller = more consistent bars)
  const stdDev = 1.0;
  
  // Generate sample data with multiple sites, each having Control and Treatment
  // Generate 20 rows per site/treatment combination for grouped bar plots
  // X-axis: site, Y-axis: value, Color: treatment
  for (let i = 0; i < 20; i++) {
    for (const site of sites) {
      for (const treatment of treatments) {
        // Start with the site's base mean
        const baseMean = siteMeans[site];
        
        // Add treatment effect if Treatment
        const mean = treatment === 'Treatment' 
          ? baseMean + treatmentEffect 
          : baseMean;
        
        // Generate realistic variation using Box-Muller transform for normal distribution
        // This creates more natural-looking data than uniform random
        const u1 = Math.random();
        const u2 = Math.random();
        const z0 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
        const randomVariation = z0 * stdDev;
        
        const value = Math.max(0, mean + randomVariation); // Ensure non-negative
        rows.push(`${site},${treatment},${value.toFixed(2)}`);
      }
    }
  }
  
  return headers + rows.join('\n');
}

/**
 * Upload sample data to the project
 */
async function uploadSampleData(projectId: string): Promise<any> {
  const csvContent = generateSampleCSV();
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const file = new File([blob], 'sample_data.csv', { type: 'text/csv' });
  
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await authorizedFetch(`/api/projects/${projectId}/upload`, {
    method: 'POST',
    body: formData,
  });
  
  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }
  
  const uploadedFile = await response.json();
  return uploadedFile;
}

/**
 * Convert tour step to Joyride Step format
 */
function convertTourStepToJoyride(
  step: TourStep,
  index: number,
  projectId?: string,
  onSampleDataUploaded?: () => void,
  _onCloseCurrentStep?: () => void,
  onRestoreTourState?: () => void,
  onAdvanceStep?: (stepIndex: number) => void,
  onSkipOnboarding?: () => void,
  onDismissTooltip?: () => void,
  showStepIndicator: boolean = false
): Step {
  const stepId = `tour-step-${step.id}`;
  const isFirstStep = step.id === 'upload-data';
  const isFinalStep = step.id === 'save-and-actions';
  // Show step indicator for steps 1-5 (indices 0-4)
  const stepNumber = index + 1;
  const totalSteps = 5;

  const isSetRolesStep = step.id === 'set-roles';
  return {
    target: step.target,
    content: (
      <div id={stepId} role="dialog" aria-labelledby={`${stepId}-title`} className="relative" style={{ paddingBottom: isFinalStep ? '8px' : isSetRolesStep ? '40px' : '50px' }}>
        {/* Close X button in top right */}
        {onDismissTooltip && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDismissTooltip();
            }}
            className="absolute top-0 right-0 -mt-1 -mr-1 h-6 w-6 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors text-gray-500 hover:text-gray-700 z-20"
            aria-label="Close tooltip"
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {/* Step indicator for steps 1-5 */}
        {showStepIndicator && index < 5 && (
          <div className="text-xs text-gray-500 mb-2 font-medium">
            Step {stepNumber}/{totalSteps}
          </div>
        )}
        {step.content.title && (
          <h3 id={`${stepId}-title`} className="font-semibold mb-2 text-base pr-6">
            {step.content.title}
          </h3>
        )}
        <p className="text-sm text-foreground mb-3 pr-6">{step.content.body}</p>
        {step.id === 'set-roles' && (
          <button
            onClick={async () => {
              try {
                // Wait for roles panel to be ready
                await waitForSelector('[data-lumi="roles-panel"]', { timeoutMs: 5000 }).catch(() => {
                  console.warn('[TourManager] Roles panel not found');
                });

                // Dispatch CustomEvent to fill roles
                // ChartBuilderCard will listen for this event and update bindings
                // Expected columns: site (X), value (Y), treatment (Series/GroupBy)
                const fillEvent = new CustomEvent('lumi:auto-fill-roles', {
                  detail: {
                    x: ['site'],
                    y: ['value'],
                    series: 'treatment',
                  },
                });
                window.dispatchEvent(fillEvent);

                // Advance to next step (grouped bars) after a short delay
                // Wait a bit longer to ensure ChartBuilderCard has processed the event
                setTimeout(() => {
                  const chooseChartIndex = onboardingSteps.findIndex(s => s.id === 'choose-chart-type');
                  console.log('[TourManager] Auto-filled roles, attempting to advance to step', chooseChartIndex, 'onAdvanceStep available:', !!onAdvanceStep);
                  
                  if (chooseChartIndex >= 0) {
                    if (onAdvanceStep) {
                      console.log('[TourManager] Calling onAdvanceStep with index', chooseChartIndex);
                      onAdvanceStep(chooseChartIndex);
                    } else {
                      console.warn('[TourManager] onAdvanceStep not available, dispatching custom event instead');
                      // Fallback: dispatch custom event that TourManager can listen for
                      window.dispatchEvent(new CustomEvent('lumi:tour-advance-step', {
                        detail: { stepIndex: chooseChartIndex }
                      }));
                    }
                  } else {
                    console.error('[TourManager] choose-chart-type step not found in onboardingSteps');
                  }
                }, 800);
              } catch (error) {
                console.error('[TourManager] Failed to auto-fill roles', error);
                const tooltip = document.querySelector(`#${stepId}`);
                if (tooltip) {
                  const existingError = tooltip.querySelector('.text-red-600');
                  if (existingError) existingError.remove();
                  const errorMsg = document.createElement('div');
                  errorMsg.className = 'text-xs text-red-600 mt-2';
                  errorMsg.textContent = 'Failed to auto-fill roles. Please fill manually.';
                  tooltip.appendChild(errorMsg);
                }
              }
            }}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium mt-2 mb-8"
          >
            Fill example data
          </button>
        )}
        {isFirstStep && (
          <button
            onClick={async () => {
              try {
                const { useProjectsStore } = await import('@/store/projects');
                const { toast } = await import('@/hooks/use-toast');

                const currentProject = useProjectsStore.getState().currentProject;
                const pid = projectId || currentProject?.id;
                console.log('[TourManager] Use sample data clicked, projectId:', pid);
                if (!pid) {
                  console.error('[TourManager] No project ID available');
                  toast({
                    title: 'Error',
                    description: 'No project ID available',
                    variant: 'destructive',
                    force: true,
                  });
                  return;
                }

                // Immediately hide step 1 by advancing to step 2 (index 1: choose-chart-type)
                // Set step index to 1 to hide the current coach card
                if (onAdvanceStep) {
                  onAdvanceStep(1);
                }
                
                // Set uploading state to show indicator in chat
                (window as any).__lumiIsSampleDataUploading = true;

                let handleFileUploaded = (window as any).__lumiHandleFileUploaded;
                console.log('[TourManager] Initial handleFileUploaded check:', !!handleFileUploaded);

                if (!handleFileUploaded) {
                  console.log('[TourManager] Waiting for handleFileUploaded to be available...');
                  for (let i = 0; i < 30; i++) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    handleFileUploaded = (window as any).__lumiHandleFileUploaded;
                    if (handleFileUploaded) {
                      console.log('[TourManager] handleFileUploaded found after', i + 1, 'attempts');
                      break;
                    }
                  }
                }

                if (!handleFileUploaded) {
                  console.error('[TourManager] handleFileUploaded not available after waiting');
                  // Clear uploading state
                  (window as any).__lumiIsSampleDataUploading = false;
                  if (onRestoreTourState) {
                    onRestoreTourState();
                  }
                  return;
                }

                console.log('[TourManager] handleFileUploaded is available, proceeding with upload');

                // Upload the file first
                let uploadedFile;
                try {
                  uploadedFile = await uploadSampleData(pid);
                  // Set file name for display in upload indicator
                  (window as any).lastDataFileName = 'sample_data.csv';
                  (window as any).lastDataOriginalName = 'sample_data.csv';
                } catch (uploadError) {
                  // Clear uploading state on error
                  (window as any).__lumiIsSampleDataUploading = false;
                  throw uploadError;
                }

                console.log('[TourManager] File uploaded, calling handleFileUploaded...', uploadedFile);

                try {
                  await handleFileUploaded(uploadedFile);
                  console.log('[TourManager] handleFileUploaded completed successfully');
                  // Clear uploading state after successful upload
                  // Delay clearing to show "Data ready" state briefly
                  setTimeout(() => {
                    (window as any).__lumiIsSampleDataUploading = false;
                  }, 2000);
                } catch (err) {
                  console.error('[TourManager] Error calling handleFileUploaded:', err);
                  // Clear uploading state on error
                  (window as any).__lumiIsSampleDataUploading = false;
                  if (onRestoreTourState) {
                    onRestoreTourState();
                  }
                  return;
                }

                const waitForFingerprint = () => {
                  return new Promise<void>((resolve) => {
                    let attempts = 0;
                    const maxAttempts = 50; // 10 seconds

                    const checkInterval = setInterval(() => {
                      attempts++;
                      const fingerprintReady = !!(window as any).__last_fp_for_chat;

                      if (fingerprintReady) {
                        console.log('[TourManager] Fingerprint ready!');
                        clearInterval(checkInterval);
                        resolve();
                      } else if (attempts >= maxAttempts) {
                        console.warn('[TourManager] Timeout waiting for fingerprint');
                        clearInterval(checkInterval);
                        resolve(); // Resolve anyway
                      }
                    }, 200);
                  });
                };

                await waitForFingerprint();

                await new Promise(resolve => setTimeout(resolve, 500));

                if (onSampleDataUploaded) {
                  onSampleDataUploaded();
                }
              } catch (error) {
                console.error('[TourManager] Failed to use sample data', error);
                // Clear uploading state on error
                (window as any).__lumiIsSampleDataUploading = false;
                if (onRestoreTourState) {
                  onRestoreTourState();
                }
              }
            }}
            className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium mt-2"
          >
            Use sample data
          </button>
        )}
        {/* CTAs for final step */}
        {isFinalStep && (step.content.primaryCTA || step.content.secondaryCTA) && (
          <div className="flex gap-2 mt-4">
            {step.content.primaryCTA && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onSkipOnboarding) {
                    onSkipOnboarding();
                  }
                }}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
                type="button"
              >
                {step.content.primaryCTA}
              </button>
            )}
            {step.content.secondaryCTA && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDismissTooltip) {
                    onDismissTooltip();
                  }
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
                type="button"
              >
                {step.content.secondaryCTA}
              </button>
            )}
          </div>
        )}
        {/* Skip button - bottom right (only show if not final step) */}
        {onSkipOnboarding && !isFinalStep && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSkipOnboarding();
            }}
            className="absolute px-3 py-1.5 text-xs font-normal text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors z-10"
            type="button"
            style={{ bottom: isSetRolesStep ? '8px' : '8px', right: '8px' }}
          >
            Skip
          </button>
        )}
      </div>
    ),
    placement: step.placement,
    disableBeacon: step.disableBeacon ?? true, // Default to true to show tooltip directly
    disableOverlayClose: step.disableOverlayClose ?? false,
    disableScrolling: step.disableScrolling ?? false,
    spotlightPadding: step.spotlightPadding ?? 8,
  };
}

/**
 * Custom styles for coach-marks
 */
const joyrideStyles: Partial<Styles> = {
  options: {
    primaryColor: '#FFD700',
    zIndex: 10000,
  },
  tooltip: {
    borderRadius: '8px',
    fontSize: '14px',
  },
  tooltipContainer: {
    textAlign: 'left' as const,
  },
  buttonNext: {
    backgroundColor: '#FFD700',
    color: '#000',
    fontSize: '14px',
    padding: '8px 16px',
    borderRadius: '6px',
    fontWeight: 500,
  },
  buttonBack: {
    display: 'none', // Hide back button - no need for it
  },
  buttonSkip: {
    display: 'none', // Hide default skip button - we have custom skip button
  },
  buttonClose: {
    display: 'none', // Hide default close button - we have custom close X button
  },
  spotlight: {
    borderRadius: '8px',
    boxShadow: '0 0 0 0.5px rgba(255, 215, 0, 0.08), 0 0 2px rgba(255, 215, 0, 0.08)',
  },
};

interface TourManagerProps {
  children: React.ReactNode;
}

export function TourManagerProvider({ children }: TourManagerProps) {
  const [isRunning, setIsRunning] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentSteps, setCurrentSteps] = useState<Step[]>([]);
  const [, setState] = useState<TourState>(loadTourState);
  const [isPostSaveMode, setIsPostSaveMode] = useState(false);
  const [serverOnboardingStatus, setServerOnboardingStatus] = useState<OnboardingStatus>('loading');
  const joyrideRef = useRef<any>(null);
  const currentStepIndexRef = useRef(currentStepIndex);
  const isRunningRef = useRef(isRunning);
  const planListenerRef = useRef<((event: Event) => void) | null>(null);
  const planObserverRef = useRef<MutationObserver | null>(null);
  const waitingForPlanCardRef = useRef(false);
  const awaitingFinalStepRef = useRef(false);
  const finalStepTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pauseReasonRef = useRef<'waiting_for_plan' | 'step3_to_step4' | 'auto_start' | 'waiting_for_final_step' | null>(null);
  const step3HighlightIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const step3HighlightedRef = useRef(false);
  const manuallyAdvancingToRef = useRef<number | null>(null);
  const { currentProject } = useProjectsStore();
  const projectId = currentProject?.id;

  // Canonical indices for key onboarding steps
  const SET_ROLES_INDEX = React.useMemo(
    () => onboardingSteps.findIndex((s) => s.id === 'set-roles'),
    []
  );
  
  const CHOOSE_CHART_INDEX = React.useMemo(
    () => onboardingSteps.findIndex((s) => s.id === 'choose-chart-type'),
    []
  );

  const SEND_MESSAGE_INDEX = React.useMemo(
    () => onboardingSteps.findIndex((s) => s.id === 'send-message'),
    []
  );

  const REVIEW_PLAN_INDEX = React.useMemo(
    () => onboardingSteps.findIndex((s) => s.id === 'review-plan'),
    []
  );

  const SAVE_AND_ACTIONS_INDEX = React.useMemo(
    () => onboardingSteps.findIndex((s) => s.id === 'save-and-actions'),
    []
  );

  // Keep refs in sync with state
  useEffect(() => {
    currentStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex]);

  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);

  /**
   * Sync onboarding status with server on mount
   */
  useEffect(() => {
    let mounted = true;

    async function syncOnboardingStatus() {
      try {
        const response = await apiRequest('GET', '/api/users/me/onboarding');
        if (!response.ok) {
          console.warn('[TourManager] Failed to fetch onboarding status, defaulting to localStorage');
          const localState = loadTourState();
          const localStatus = localState.status === 'completed' || localState.status === 'dismissed'
            ? localState.status
            : 'new';
          if (mounted) {
            setServerOnboardingStatus(localStatus);
          }
          return;
        }

        const data = await response.json();
        const serverStatus = (data.status === 'completed' || data.status === 'dismissed')
          ? data.status
          : 'new';

        if (!mounted) return;

        // If server says completed/dismissed, sync to localStorage
        if (serverStatus === 'completed' || serverStatus === 'dismissed') {
          const localState = loadTourState();
          
          // Check if dismissed status has expired snooze
          if (serverStatus === 'dismissed') {
            const snoozeUntilStr = localStorage.getItem(STORAGE_KEY_SNOOZE);
            const snoozeUntil = snoozeUntilStr ? parseInt(snoozeUntilStr, 10) : undefined;
            
            // If snooze expired, reset to 'new' and sync to server
            if (snoozeUntil && Date.now() >= snoozeUntil) {
              const resetState: TourState = { status: 'new', step: 0, tourId: null };
              saveTourState(resetState);
              setState(resetState);
              setServerOnboardingStatus('new');
              
              // Sync reset to server
              try {
                await apiRequest('POST', '/api/users/me/onboarding', {
                  status: 'new',
                });
              } catch (err) {
                console.error('[TourManager] Failed to sync expired snooze reset to server', err);
              }
              return;
            }
          }
          
          if (localState.status !== serverStatus) {
            const newState: TourState = {
              status: serverStatus,
              step: localState.step,
              tourId: null,
              snoozeUntil: serverStatus === 'dismissed' ? localState.snoozeUntil : undefined,
            };
            saveTourState(newState);
            setState(newState);
          }
          setServerOnboardingStatus(serverStatus);
        } else {
          // Server says 'new', check localStorage for legacy completion/dismissal
          const localState = loadTourState();
          if (localState.status === 'completed' || localState.status === 'dismissed') {
            // Legacy localStorage value exists, sync to server
            try {
              await apiRequest('POST', '/api/users/me/onboarding', {
                status: localState.status,
              });
              // Server updated, keep the status
              setServerOnboardingStatus(localState.status);
            } catch (err) {
              console.error('[TourManager] Failed to sync legacy status to server', err);
              setServerOnboardingStatus('new');
            }
          } else {
            setServerOnboardingStatus('new');
          }
        }
      } catch (error) {
        console.error('[TourManager] Error syncing onboarding status', error);
        if (mounted) {
          // Fallback to localStorage
          const localState = loadTourState();
          const localStatus = localState.status === 'completed' || localState.status === 'dismissed'
            ? localState.status
            : 'new';
          setServerOnboardingStatus(localStatus);
        }
      }
    }

    syncOnboardingStatus();

    return () => {
      mounted = false;
    };
  }, []);

  /**
   * Centralized cleanup for plan card watcher
   * Removes event listener and MutationObserver
   */
  const stopWaitingForPlanCard = useCallback(() => {
    if (planListenerRef.current) {
      window.removeEventListener('lumiplot-plan-ready', planListenerRef.current);
      planListenerRef.current = null;
    }

    if (planObserverRef.current) {
      planObserverRef.current.disconnect();
      planObserverRef.current = null;
    }

    waitingForPlanCardRef.current = false;
    // Clear pause reason if it was set for waiting for plan
    if (pauseReasonRef.current === 'waiting_for_plan' || pauseReasonRef.current === 'step3_to_step4') {
      pauseReasonRef.current = null;
    }
  }, []);

  /**
   * Clear waiting state for final step
   * Cancels timeout and resets refs
   */
  const clearFinalStepWaitingState = useCallback(() => {
    if (finalStepTimeoutRef.current) {
      clearTimeout(finalStepTimeoutRef.current);
      finalStepTimeoutRef.current = null;
    }
    awaitingFinalStepRef.current = false;
    // Only clear pause reason if it was set for waiting for final step
    if (pauseReasonRef.current === 'waiting_for_final_step') {
      pauseReasonRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopWaitingForPlanCard();
      clearFinalStepWaitingState();
    };
  }, [stopWaitingForPlanCard, clearFinalStepWaitingState]);

  /**
   * Wait for plan card to appear and restart tour at Step 4 (review-plan)
   * 
   * Pure event-driven approach: Only reacts to 'lumiplot-plan-ready' event.
   * No timeouts - can wait arbitrarily long for the plan to arrive.
   * Uses MutationObserver only after event fires, just in case button isn't in DOM on first frame.
   */
  const waitForPlanCard = useCallback(() => {
    const reviewPlanIndex = onboardingSteps.findIndex(
      (step) => step.id === 'review-plan'
    );

    if (reviewPlanIndex < 0) {
      console.warn('[TourManager] waitForPlanCard: review-plan step not found');
      return;
    }

    // 🔑 CRITICAL: Guard against restarting plan watcher when we're waiting for final step artifacts
    // This prevents the hydration effect from overwriting the 'waiting_for_final_step' pause reason
    if (awaitingFinalStepRef.current) {
      console.log('[TourManager] waitForPlanCard: skipping - awaiting final step artifacts, not plan card');
      return;
    }

    const storedState = loadTourState();

    console.log('[TourManager] waitForPlanCard: listening for lumiplot-plan-ready event', {
      currentRefStep: currentStepIndexRef.current,
      reviewPlanIndex,
      storedStep: storedState.step,
    });

    // Don't register multiple listeners
    if (waitingForPlanCardRef.current) {
      console.log('[TourManager] waitForPlanCard: already waiting, skipping re-register');
      return;
    }
    waitingForPlanCardRef.current = true;
    // Set pause reason for waiting for plan card
    pauseReasonRef.current = 'waiting_for_plan';

    // Check if plan card already exists (race condition: event fired before listener attached)
    const existingButton = document.querySelector<HTMLElement>(
      '[data-lumi="btn-plan-recommended"]'
    );
    if (existingButton) {
      console.log('[TourManager] waitForPlanCard: plan card already exists, resuming immediately');
      // Check if tour is still active (not completed/dismissed)
      const storedState = loadTourState();
      if (storedState.status === 'completed' || storedState.status === 'dismissed') {
        console.log(
          '[TourManager] plan card exists but tour already completed/dismissed, ignoring',
          { status: storedState.status }
        );
        stopWaitingForPlanCard();
        return;
      }
      
      // Rebuild steps and wait for target before resuming (same pattern as startTour)
                const updatedSteps = onboardingSteps.map((s, i) =>
                  convertTourStepToJoyride(s, i, projectId, undefined, undefined, undefined, handleAdvanceStep, skip, dismissTooltip, true)
                );
      setCurrentSteps(updatedSteps);
      
      // Wait for target to be confirmed before resuming
      waitForTargets(updatedSteps, reviewPlanIndex).then((targetFound) => {
        if (!targetFound) {
          console.warn('[TourManager] waitForPlanCard: target not found after rebuild, bailing');
          stopWaitingForPlanCard();
          return;
        }
        
        // Target confirmed, now resume tour
        stopWaitingForPlanCard();
        
        // Clear pause reason since we're resuming
        pauseReasonRef.current = null;
        
        // Update persisted state to step 4
        const newState: TourState = {
          status: 'in_progress',
          step: reviewPlanIndex,
          tourId: 'onboarding',
          snoozeUntil: storedState.snoozeUntil,
        };
        setState(newState);
        saveTourState(newState);
        
        // Set step index
        setCurrentStepIndex(reviewPlanIndex);
        
        // Add highlight class after target is confirmed
        const targetElement = document.querySelector<HTMLElement>(
          '[data-lumi="btn-plan-recommended"]'
        );
        if (targetElement) {
          targetElement.classList.add('tour-highlight-active');
        }
        
        // Add delay before showing tooltip to allow button to settle into stable position
        setTimeout(() => {
          setIsRunning(true);
          console.log('[TourManager] Resuming tour at step 4 (review-plan) - button already exists');
        }, 400);
      });
      return;
    }

    const resumeAtReviewPlan = () => {
      // Guard against multiple resumptions (race condition if event fires multiple times)
      if (!waitingForPlanCardRef.current) {
        console.log('[TourManager] resumeAtReviewPlan: already resumed, ignoring duplicate call');
        return;
      }

      // Check if tour is still active (not completed/dismissed)
      const storedState = loadTourState();
      if (storedState.status === 'completed' || storedState.status === 'dismissed') {
        console.log(
          '[TourManager] plan-ready received but tour already completed/dismissed, ignoring',
          { status: storedState.status }
        );
        stopWaitingForPlanCard();
        return;
      }

      console.log('[TourManager] Resuming tour at review-plan step', {
        reviewPlanIndex,
        currentRefStep: currentStepIndexRef.current,
        storedStep: storedState.step,
      });

      stopWaitingForPlanCard(); // clears listener + observer and sets waitingForPlanCardRef.current = false

      // Clear pause reason since we're resuming
      pauseReasonRef.current = null;

      // Rebuild steps and wait for target before resuming (same pattern as startTour)
                const updatedSteps = onboardingSteps.map((s, i) =>
                  convertTourStepToJoyride(s, i, projectId, undefined, undefined, undefined, handleAdvanceStep, skip, dismissTooltip, true)
                );
      setCurrentSteps(updatedSteps);
      
      // Wait for target to be confirmed before resuming
      waitForTargets(updatedSteps, reviewPlanIndex).then((targetFound) => {
        if (!targetFound) {
          console.warn('[TourManager] resumeAtReviewPlan: target not found after rebuild, bailing');
          return;
        }
        
        // Target confirmed, now resume tour
        // Update persisted state to step 4
        const newState: TourState = {
          status: 'in_progress',
          step: reviewPlanIndex,
          tourId: 'onboarding',
          snoozeUntil: storedState.snoozeUntil,
        };
        setState(newState);
        saveTourState(newState);

        // Set step index
        setCurrentStepIndex(reviewPlanIndex);

        // Add highlight class after target is confirmed
        const targetElement = document.querySelector<HTMLElement>(
          '[data-lumi="btn-plan-recommended"]'
        );
        if (targetElement) {
          targetElement.classList.add('tour-highlight-active');
        }

        // Add delay before showing tooltip to allow button to settle into stable position
        setTimeout(() => {
          setIsRunning(true);
          console.log('[TourManager] Resuming tour at step 4 (review-plan)');
        }, 400);
      });
    };

    const attachOrObserveButton = () => {
      const approveButton = document.querySelector<HTMLElement>(
        '[data-lumi="btn-plan-recommended"]',
      );

      if (approveButton) {
        console.log('[TourManager] approve button found, resuming tour at review-plan');
        resumeAtReviewPlan();
        return;
      }

      console.log(
        '[TourManager] plan-ready fired but approve button not yet in DOM, starting MutationObserver',
      );

      const observer = new MutationObserver(() => {
        const btn = document.querySelector<HTMLElement>('[data-lumi="btn-plan-recommended"]');
        if (btn) {
          console.log(
            '[TourManager] approve button appeared after mutation, resuming tour at review-plan',
          );
          observer.disconnect();
          planObserverRef.current = null;
          resumeAtReviewPlan();
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
      planObserverRef.current = observer;
    };

    const handlePlanReady = (event: Event) => {
      console.log('[TourManager] lumiplot-plan-ready fired', event);
      // Give React a frame to actually render the plan card
      requestAnimationFrame(attachOrObserveButton);
    };

    planListenerRef.current = handlePlanReady;
    window.addEventListener('lumiplot-plan-ready', handlePlanReady);
    // Note: waitForTargets is intentionally not in dependencies - it's defined later but is stable (useCallback with no deps)
  }, [setCurrentStepIndex, setIsRunning, stopWaitingForPlanCard, projectId, setCurrentSteps, REVIEW_PLAN_INDEX]);

  // Load state on mount
  useEffect(() => {
    const loadedState = loadTourState();
    
    // Don't reset if tour is completed or dismissed
    if (loadedState.status === 'completed' || loadedState.status === 'dismissed') {
      setState(loadedState);
      return;
    }
    
    // Check if tour is stuck (in_progress but not running)
    // If tour state says "in_progress" but tour isn't actually running, check if it's a valid paused state
    if (loadedState.status === 'in_progress' && !isRunning) {
      // 🔑 CRITICAL: Check if stored step is SAVE_AND_ACTIONS_INDEX - this indicates we're waiting for final step artifacts
      // This marker was set by advanceFromPlanToFinalStep() to prevent hydration from restarting Step 4 watchers
      if (loadedState.step === SAVE_AND_ACTIONS_INDEX) {
        console.log('[TourManager] Restoring paused tour - waiting for final step artifacts (step marker:', SAVE_AND_ACTIONS_INDEX, ')');
        // Restore awaitingFinalStepRef if it was cleared (defensive)
        awaitingFinalStepRef.current = true;
        pauseReasonRef.current = 'waiting_for_final_step';
        setCurrentStepIndex(REVIEW_PLAN_INDEX); // Keep UI at Step 4, but state marker indicates waiting for final step
        return;
      }
      
      // ✅ Legitimate paused state: step 4 waits for plan card
      // This is NOT a stuck state - the step-4 watcher will resume the tour when the plan card appears
      if (loadedState.step === REVIEW_PLAN_INDEX) {
        // 🔑 CRITICAL: Check if we're waiting for final step artifacts before restarting plan watcher
        // If awaitingFinalStepRef is true, we're in the "waiting for artifacts" state after approval,
        // NOT waiting for the plan card. Do NOT restart waitForPlanCard() in this case.
        if (awaitingFinalStepRef.current) {
          console.log('[TourManager] Restoring paused tour at step 4 but awaiting final step artifacts - NOT restarting plan watcher');
          setCurrentStepIndex(REVIEW_PLAN_INDEX);
          return;
        }
        
        console.log('[TourManager] Restoring paused tour at step 4 (waiting for plan card), not resetting');
        setCurrentStepIndex(REVIEW_PLAN_INDEX);
        // 🔑 Re-attach watcher when we hydrate in a paused Step 4 state (only if NOT awaiting final step)
        waitForPlanCard();
        return;
      }
      
      // If tour is stuck at step 0 (first step) but not running, reset to 'new' so it can auto-start
      // This handles cases where the tour was interrupted before it could start
      if (loadedState.step === 0) {
        console.log('[TourManager] Tour stuck at step 0 but not running - resetting to new state');
        const resetState: TourState = { status: 'new', step: 0, tourId: null };
        setState(resetState);
        saveTourState(resetState);
        setCurrentStepIndex(0);
        return;
      }
      
      // For other steps, leave as-is to avoid premature resets
      console.log('[TourManager] Tour in_progress at step', loadedState.step, 'but not running - leaving as-is (no auto reset)');
      setCurrentStepIndex(loadedState.step ?? 0);
      return;
    }
    
    setState(loadedState);
    if (loadedState.status === 'in_progress' && loadedState.step > 0) {
      setCurrentStepIndex(loadedState.step);
    }
  }, [isRunning, REVIEW_PLAN_INDEX, SAVE_AND_ACTIONS_INDEX, waitForPlanCard]);

  /**
   * Wait for target element to exist in DOM
   */
  const waitForTargets = useCallback((steps: Step[], startIndex: number): Promise<boolean> => {
    return new Promise((resolve) => {
      const target = steps[startIndex]?.target as string;
      if (!target) {
        resolve(false);
        return;
      }

      // Check immediately
      if (document.querySelector(target)) {
        resolve(true);
        return;
      }

      // Use MutationObserver to watch for DOM changes
      const observer = new MutationObserver(() => {
        if (document.querySelector(target)) {
          observer.disconnect();
          resolve(true);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        observer.disconnect();
        const found = !!document.querySelector(target);
        console.log(`[TourManager] Target "${target}" ${found ? 'found' : 'not found'} after timeout`);
        resolve(found);
      }, 10000);
    });
  }, []);

  /**
   * Dismiss tour (with 7-day snooze)
   */
  const dismiss = useCallback(async () => {
    // Clear any pending final step waiting state
    clearFinalStepWaitingState();
    // Clear pause reason when dismissing
    pauseReasonRef.current = null;

    const snoozeUntil = Date.now() + SNOOZE_DURATION_MS;
    const newState: TourState = {
      status: 'dismissed',
      step: currentStepIndex,
      tourId: null,
      snoozeUntil,
    };
    setState(newState);
    saveTourState(newState);
    setIsRunning(false);
    setServerOnboardingStatus('dismissed');
    trackOnboardingEvent('onboarding:dismissed', { step_id: onboardingSteps[currentStepIndex]?.id });

    // Sync to server
    try {
      await apiRequest('POST', '/api/users/me/onboarding', {
        status: 'dismissed',
      });
    } catch (error) {
      console.error('[TourManager] Failed to sync dismissal to server', error);
    }
  }, [currentStepIndex, clearFinalStepWaitingState]);

  /**
   * Skip entire onboarding (marks as completed)
   */
  const skip = useCallback(async () => {
    // Clear any pending final step waiting state
    clearFinalStepWaitingState();
    // Clear pause reason when skipping
    pauseReasonRef.current = null;

    const newState: TourState = {
      status: 'completed',
      step: onboardingSteps.length - 1,
      tourId: 'onboarding',
    };
    setState(newState);
    saveTourState(newState);
    setIsRunning(false);
    setServerOnboardingStatus('completed');
    trackOnboardingEvent('onboarding:completed', { step_id: onboardingSteps[currentStepIndex]?.id, skipped: true });

    // Sync to server
    try {
      await apiRequest('POST', '/api/users/me/onboarding', {
        status: 'completed',
      });
    } catch (error) {
      console.error('[TourManager] Failed to sync completion to server', error);
    }
  }, [currentStepIndex, clearFinalStepWaitingState]);

  /**
   * Dismiss current tooltip without skipping onboarding
   */
  const dismissTooltip = useCallback(() => {
    // Just hide the tooltip, but keep tour state as 'in_progress'
    // This allows the tour to resume later if needed
    setIsRunning(false);
    // Use dismissed event but note it's just tooltip dismissal, not full tour dismissal
    trackOnboardingEvent('onboarding:dismissed', { step_id: onboardingSteps[currentStepIndex]?.id, tooltip_only: true });
  }, [currentStepIndex]);

  /**
   * Helper function to advance to a specific step
   * Defined after skip and dismissTooltip so it can reference them
   */
  const handleAdvanceStep = useCallback((stepIndex: number) => {
    const targetStep = onboardingSteps[stepIndex];
    if (!targetStep) {
      console.warn('[TourManager] Invalid step index:', stepIndex);
      return;
    }

    console.log('[TourManager] Advancing to step', stepIndex, `(${targetStep.id})`);
    
    // Remove highlight from role-x when advancing past step 2 (set-roles)
    if (targetStep.id !== 'set-roles') {
      const roleX = document.querySelector('[data-lumi="role-x"]');
      if (roleX) {
        roleX.classList.remove('tour-highlight-active');
      }
    }
    
    // Mark that we're manually advancing to this step
    manuallyAdvancingToRef.current = stepIndex;
    
    // Update steps
    const updatedSteps = onboardingSteps.map((s, i) =>
      convertTourStepToJoyride(s, i, projectId, undefined, undefined, undefined, handleAdvanceStep, skip, dismissTooltip, true)
    );
    setCurrentSteps(updatedSteps);
    
    // Update step index
    setCurrentStepIndex(stepIndex);
    
    // Update state
    const newState: TourState = { status: 'in_progress', step: stepIndex, tourId: 'onboarding' };
    setState(newState);
    saveTourState(newState);
    
    // Wait for target and show tooltip
    waitForTargets(updatedSteps, stepIndex).then((targetFound) => {
      if (targetFound) {
        console.log('[TourManager] Target found for step', stepIndex, ', showing tooltip');
        setIsRunning(true);
      } else {
        console.warn('[TourManager] Target not found for step', stepIndex);
        setIsRunning(true); // Show anyway
      }
      // Clear the manual advancement flag after a delay to allow Joyride to sync
      // This gives Joyride time to catch up if it tries to auto-advance
      setTimeout(() => {
        if (manuallyAdvancingToRef.current === stepIndex) {
          console.log('[TourManager] Clearing manual advancement flag for step', stepIndex);
          manuallyAdvancingToRef.current = null;
        }
      }, 1000);
    });
  }, [onboardingSteps, projectId, setCurrentSteps, setCurrentStepIndex, setState, saveTourState, waitForTargets, skip, dismissTooltip, setIsRunning]);

  /**
   * Start the tour
   */
  const startTour = useCallback((id: TourId, force: boolean = false) => {
    console.log('[TourManager] startTour called', { id, force, isRunning: isRunningRef.current });
    if (id !== 'onboarding') {
      console.log('[TourManager] startTour: invalid tour id, returning');
      return;
    }

    // Clear any pending final step waiting state
    clearFinalStepWaitingState();
    // Clear pause reason when starting tour
    pauseReasonRef.current = null;

    // 🚫 Never restart while Joyride is already running
    if (isRunningRef.current) {
      console.log('[TourManager] startTour called while tour already running - ignoring');
      return;
    }

    const currentState = loadTourState();
    console.log('[TourManager] startTour: current state', currentState);

    // If tour is stuck at step 0 (in_progress but not running), reset it first
    if (currentState.status === 'in_progress' && currentState.step === 0 && !isRunningRef.current) {
      console.log('[TourManager] startTour: detected stuck tour at step 0, resetting to new');
      const resetState: TourState = { status: 'new', step: 0, tourId: null };
      setState(resetState);
      saveTourState(resetState);
      // Continue with startTour logic below
    }

    // Only allow auto-start (force = false) when status is 'new'
    // Re-check state after potential reset above
    // Testing override: bypass status check when forced
    const finalState = loadTourState();
    const forced = force || isTourForced();
    if (!forced && finalState.status !== 'new') {
      console.log('[TourManager] startTour: skipping auto-start, status =', finalState.status, 'step =', finalState.step);
      return;
    }

    // If forcing restart, *only* let it override 'completed' / 'dismissed',
    // NOT an active 'in_progress' state (unless it's stuck at step 0, which we already handled)
    if (forced && finalState.status === 'in_progress' && finalState.step > 0) {
      console.log('[TourManager] startTour(force) called while in_progress at step > 0, ignoring to avoid reset mid-tour');
      return;
    }

    // Prevent restarting if already completed (unless forced for testing)
    if (finalState.status === 'completed' && !forced) {
      console.log('[TourManager] Tour already completed, not restarting');
      return;
    }

    // If forcing restart, reset the state (only for completed/dismissed)
    if (forced && (finalState.status === 'completed' || finalState.status === 'dismissed')) {
      console.log('[TourManager] Force restarting tour (testing mode)');
      const resetState: TourState = { status: 'new', step: 0, tourId: null };
      setState(resetState);
      saveTourState(resetState);
    }

    const steps = onboardingSteps.map((step, idx) =>
      convertTourStepToJoyride(
        step,
        idx,
        projectId,
        () => {
          // Callback when sample data is uploaded - go directly to chart builder
          // Step 1 (index 0): Add your data - COMPLETED
          // Step 2 (index 1): Choose chart type - NEXT
          const nextStep = 1; // Go to chart builder (Step 2, index 1)

          console.log('[TourManager] Sample data uploaded, advancing to step', nextStep, '(Step 2: Choose chart type)');
          
          // CRITICAL: Hide step 0 tooltip immediately by advancing currentStepIndex
          // This ensures the first tooltip disappears before showing the second one
          // Use functional update to ensure we get the latest value
          setCurrentStepIndex((prev) => {
            console.log('[TourManager] Setting currentStepIndex from', prev, 'to', nextStep);
            return nextStep;
          });
          
          // Also update steps immediately to ensure react-joyride has the latest steps
                const updatedSteps = onboardingSteps.map((s, i) =>
                  convertTourStepToJoyride(s, i, projectId, undefined, undefined, undefined, handleAdvanceStep, skip, dismissTooltip, true)
                );
          setCurrentSteps(updatedSteps);

          const checkChartBuilder = () => {
            return new Promise<void>((resolve) => {
              let attempts = 0;
              const maxAttempts = 75; // 15 seconds - chart builder might take longer to render

              const checkInterval = setInterval(() => {
                attempts++;
                const fingerprintReady = !!(window as any).__last_fp_for_chat;

                const chartBuilder = document.querySelector('[data-lumi="chart-builder"]');
                const rolesPanel = document.querySelector('[data-lumi="roles-panel"]');
                const roleX = document.querySelector('[data-lumi="role-x"]');

                if (fingerprintReady && roleX && rolesPanel) {
                  const accordionItems = Array.from(document.querySelectorAll('[role="region"]'));
                  for (const item of accordionItems) {
                    const trigger = item.querySelector('[role="button"]');
                    if (trigger && trigger.textContent?.includes('Compare')) {
                      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
                      if (!isExpanded) {
                        console.log('[TourManager] Expanding Compare section');
                        (trigger as HTMLElement).click();
                        setTimeout(() => {
                          const roleXAfterExpand = document.querySelector('[data-lumi="role-x"]');
                          if (roleXAfterExpand) {
                            console.log('[TourManager] Role X found after expanding');
                            clearInterval(checkInterval);
                            resolve();
                          }
                        }, 300);
                      }
                    }
                  }
                }

                console.log(`[TourManager] Checking for chart builder (attempt ${attempts}/${maxAttempts}):`, {
                  chartBuilderFound: !!chartBuilder,
                  rolesPanelFound: !!rolesPanel,
                  roleXFound: !!roleX,
                  fingerprintReady,
                });

                if (chartBuilder && rolesPanel && fingerprintReady) {
                  console.log('[TourManager] Chart builder ready, advancing tour');
                  clearInterval(checkInterval);
                  resolve();
                } else if (fingerprintReady && rolesPanel && attempts >= 10) {
                  console.log('[TourManager] Fingerprint ready and roles panel found, advancing');
                  clearInterval(checkInterval);
                  resolve();
                } else if (attempts >= maxAttempts) {
                  console.warn('[TourManager] Timeout waiting for chart builder, advancing anyway');
                  clearInterval(checkInterval);
                  resolve();
                }
              }, 200);
            });
          };

          checkChartBuilder().then(() => {
            // Wait for the target element to be found before showing the step
            const targetSelector = onboardingSteps[nextStep]?.target;
            if (targetSelector) {
              // CRITICAL: Update steps FIRST, then currentStepIndex
              // This ensures react-joyride has the latest steps before we change the stepIndex
                const updatedSteps = onboardingSteps.map((s, i) =>
                  convertTourStepToJoyride(s, i, projectId, undefined, undefined, undefined, handleAdvanceStep, skip, dismissTooltip, true)
                );
              setCurrentSteps(updatedSteps);
              
              // Then update currentStepIndex - this will trigger a re-render with the correct stepIndex
              console.log('[TourManager] Setting currentStepIndex to', nextStep, '(Step 2) before waiting for targets');
              setCurrentStepIndex(nextStep);
              
              // Update state
              const newState: TourState = { status: 'in_progress', step: nextStep, tourId: 'onboarding' };
              setState(newState);
              saveTourState(newState);
              
              // CRITICAL: Always set isRunning to true to ensure highlight useEffect runs
              // Even if it's already true, this ensures React re-evaluates the effect
              setIsRunning(true);
              
              waitForTargets(updatedSteps, nextStep).then((targetFound) => {
                if (targetFound) {
                  console.log('[TourManager] Target found for step', nextStep, ', showing tooltip');
                  
                  // Ensure currentStepIndex is still set to nextStep
                  setCurrentStepIndex((prev) => {
                    if (prev !== nextStep) {
                      console.log('[TourManager] currentStepIndex was', prev, ', resetting to', nextStep);
                      return nextStep;
                    }
                    return prev;
                  });
                  
                  // CRITICAL: Always set isRunning to true to ensure highlight useEffect runs
                  setIsRunning(true);
                  
                  // Small delay to ensure DOM is ready and react-joyride can find the target
                  setTimeout(() => {
                    // Verify target element is in DOM
                    const element = document.querySelector('[data-lumi="role-x"]');
                    if (element) {
                      console.log('[TourManager] Target element confirmed in DOM, tooltip should show');
                      
                      // CRITICAL: Force highlight to be added here as well, in case the useEffect hasn't run yet
                      // This ensures the highlight is added even if there's a timing issue with the useEffect
                      if (!element.classList.contains('tour-highlight-active')) {
                        console.log('[TourManager] Adding highlight directly after target confirmation');
                        element.classList.add('tour-highlight-active');
                      }
                      
                      // Ensure element is in viewport without scrolling
                      const rect = element.getBoundingClientRect();
                      const isInViewport = rect.top >= 0 && rect.left >= 0 && 
                                         rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                                         rect.right <= (window.innerWidth || document.documentElement.clientWidth);
                      
                      if (!isInViewport) {
                        // Element is not fully visible, but don't scroll - let react-joyride handle positioning
                        console.log('[TourManager] Target element not fully in viewport, react-joyride will handle positioning');
                      }
                    } else {
                      console.warn('[TourManager] Target element not found in DOM');
                    }
                  }, 150);

                  trackOnboardingEvent('onboarding:step_shown', { step_id: onboardingSteps[nextStep]?.id });
                } else {
                  console.warn('[TourManager] Target not found for step', nextStep, ', tooltip may not show');
                  // Still advance the tour, but tooltip might not show
                  const newState: TourState = { status: 'in_progress', step: nextStep, tourId: 'onboarding' };
                  setState(newState);
                  saveTourState(newState);

                const updatedSteps = onboardingSteps.map((s, i) =>
                  convertTourStepToJoyride(s, i, projectId, undefined, undefined, undefined, handleAdvanceStep, skip, dismissTooltip, true)
                );
                  setCurrentSteps(updatedSteps);
                  setCurrentStepIndex(nextStep);
                  setIsRunning(true);

                  trackOnboardingEvent('onboarding:step_shown', { step_id: onboardingSteps[nextStep]?.id });
                }
              });
            } else {
              // No target selector, just advance
              const newState: TourState = { status: 'in_progress', step: nextStep, tourId: 'onboarding' };
              setState(newState);
              saveTourState(newState);

                const updatedSteps = onboardingSteps.map((s, i) =>
                  convertTourStepToJoyride(s, i, projectId, undefined, undefined, undefined, handleAdvanceStep, skip, dismissTooltip, true)
                );
              setCurrentSteps(updatedSteps);
              setCurrentStepIndex(nextStep);
              setIsRunning(true);

              trackOnboardingEvent('onboarding:step_shown', { step_id: onboardingSteps[nextStep]?.id });
            }
          });
        },
        () => {
          // Callback to close current step immediately
          console.log('[TourManager] Closing current step');
          setIsRunning(false); // Stop tour temporarily
        },
        () => {
          // Callback to restore tour state on error
          console.log('[TourManager] Restoring tour state');
          setIsRunning(true);
          setCurrentStepIndex(0);
        },
        handleAdvanceStep,
        skip,
        dismissTooltip,
        true // showStepIndicator for onboarding steps
      )
    );
    console.log('[TourManager] startTour: creating steps and setting up tour');
    setCurrentSteps(steps);
    setCurrentStepIndex(0);
    setState({ status: 'in_progress', step: 0, tourId: id });
    saveTourState({ status: 'in_progress', step: 0, tourId: id });

    console.log('[TourManager] startTour: waiting for targets, first target:', steps[0]?.target);
    waitForTargets(steps, 0).then((ready) => {
      console.log('[TourManager] startTour: target check complete', { ready, target: steps[0]?.target });
      if (ready) {
        console.log('[TourManager] Targets found, starting tour');
        // CRITICAL: Ensure currentStepIndex is 0 when tour starts
        setCurrentStepIndex(0);
        setIsRunning(true);
        console.log('[TourManager] Tour is now running, setIsRunning(true) called');
        trackOnboardingEvent('onboarding:started');
      } else {
        console.warn('[TourManager] Targets not found, tour not started. First target:', steps[0]?.target);
        steps.forEach((step, idx) => {
          const el = document.querySelector(step.target as string);
          console.log(`[TourManager] Step ${idx} target "${step.target}":`, el ? 'FOUND' : 'NOT FOUND');
        });
        setState({ status: 'new', step: 0, tourId: null });
        saveTourState({ status: 'new', step: 0, tourId: null });
      }
    });
  }, [waitForTargets, projectId, handleAdvanceStep, onboardingSteps, setCurrentSteps, setCurrentStepIndex, setState, saveTourState, skip, dismissTooltip, setIsRunning, clearFinalStepWaitingState]);

  /**
   * Resume tour if needed
   */
  const resumeIfNeeded = useCallback(() => {
    // Clear any pending final step waiting state
    clearFinalStepWaitingState();
    // Clear pause reason when resuming
    pauseReasonRef.current = null;

    const loadedState = loadTourState();
    if (loadedState.status === 'new') {
      startTour('onboarding');
      return;
    }
    if (loadedState.status === 'in_progress' && loadedState.step > 0) {
      const steps = onboardingSteps.map((step, idx) =>
        convertTourStepToJoyride(step, idx, projectId, undefined, undefined, undefined, undefined, skip, dismissTooltip, true)
      );
      setCurrentSteps(steps);
      setCurrentStepIndex(loadedState.step);
      setIsRunning(true);
    }
  }, [startTour, projectId, clearFinalStepWaitingState]);

  /**
   * Handle Joyride callbacks
   */
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { action, index, status, type } = data;

    console.log('[TourManager] Joyride callback:', { action, index, status, type });

    // 🔐 Ignore Joyride's synthetic "finished/skipped" when we're in a controlled pause.
    // This is the critical bit for Step 4 → final step.
    // 🔑 CRITICAL: Also check awaitingFinalStepRef directly to catch cases where pauseReasonRef
    // might have been cleared by waitForPlanCard() but we're still waiting for artifacts
    const isInControlledPause =
      awaitingFinalStepRef.current ||
      pauseReasonRef.current === 'waiting_for_final_step' ||
      pauseReasonRef.current === 'waiting_for_plan' ||
      pauseReasonRef.current === 'step3_to_step4';

    if ((status === 'finished' || status === 'skipped') && isInControlledPause) {
      console.log(
        '[TourManager] Joyride reported',
        status,
        'while paused (reason:',
        pauseReasonRef.current,
        ', awaitingFinalStep:',
        awaitingFinalStepRef.current,
        ') - treating as internal no-op'
      );
      // ❗ Do NOT clear awaitingFinalStepRef or pauseReason here.
      // We want analysis:plot_built / plan-ready to resume the tour.
      return;
    }

      if (status === 'finished' || status === 'skipped') {
        console.log('[TourManager] Tour finished or skipped, stopping tour', { status, currentStepIndex });
        
        // 🔑 CRITICAL: Gate cleanup on step change - ignore transient skipped/finished while on manual-advance steps
        // Joyride emits skipped/finished during pause, but we're still on those steps
        // Only stop the tour if we're actually leaving those steps
        if ((status === 'finished' || status === 'skipped') && (
          currentStepIndex === SET_ROLES_INDEX || 
          currentStepIndex === CHOOSE_CHART_INDEX || 
          currentStepIndex === SEND_MESSAGE_INDEX
        )) {
          const targetStep = onboardingSteps[currentStepIndex];
          if (targetStep?.id === 'set-roles' || targetStep?.id === 'choose-chart-type' || targetStep?.id === 'send-message') {
            console.log('[TourManager] Ignoring transient', status, 'event while on Step', currentStepIndex + 1, '- still on step');
            return;
          }
        }
        
        // Clear any pending final step waiting state
        clearFinalStepWaitingState();
        // Clear pause reason when tour finishes
        pauseReasonRef.current = null;
        
        if (status === 'finished') {
          const newState: TourState = { status: 'completed', step: onboardingSteps.length - 1, tourId: 'onboarding' };
          setState(newState);
          saveTourState(newState);
          setServerOnboardingStatus('completed');
          trackOnboardingEvent('onboarding:completed');

          // Sync to server
          apiRequest('POST', '/api/users/me/onboarding', {
            status: 'completed',
          }).catch((error) => {
            console.error('[TourManager] Failed to sync completion to server', error);
          });
        }
        
        // Always stop the tour when finished/skipped to unblock interactions
        setIsRunning(false);
        setIsPostSaveMode(false);
        console.log('[TourManager] Tour stopped, interactions should be unblocked');
        return;
      }
      
      // Prevent tour from stopping if we're paused at manual-advance steps
      // These steps are paused when tooltip appears to allow user interaction
      // Tour will restart when appropriate action is taken (chart selected, message sent, etc.)
      if (status === 'paused' && (
        currentStepIndex === SET_ROLES_INDEX || 
        currentStepIndex === CHOOSE_CHART_INDEX || 
        currentStepIndex === SEND_MESSAGE_INDEX
      )) {
        const targetStep = onboardingSteps[currentStepIndex];
        if (targetStep?.id === 'set-roles' || targetStep?.id === 'choose-chart-type' || targetStep?.id === 'send-message') {
          console.log('[TourManager] Tour paused at step', currentStepIndex + 1, '- allowing user interaction');
          return;
        }
      }

    // Only handle step advancement on actual step transitions (step:after), not on tour:status callbacks
    if (type === 'step:after' && (action === 'next' || action === 'prev')) {
      const currentStep = onboardingSteps[index];
      const nextIndex = action === 'next' ? index + 1 : index - 1;
      
      // If we're manually advancing to a specific step, allow Joyride to sync to that step
      if (manuallyAdvancingToRef.current !== null && nextIndex === manuallyAdvancingToRef.current) {
        console.log(
          '[TourManager] Allowing Joyride to sync to manually advanced step',
          nextIndex,
          `(${onboardingSteps[nextIndex]?.id})`
        );
        // Clear the ref now that Joyride has synced
        manuallyAdvancingToRef.current = null;
        // Allow the advance to proceed - don't prevent it
        setCurrentStepIndex(nextIndex);
        const newState: TourState = { status: 'in_progress', step: nextIndex, tourId: 'onboarding' };
        setState(newState);
        saveTourState(newState);
        trackOnboardingEvent('onboarding:step_shown', { step_id: onboardingSteps[nextIndex]?.id });
        return;
      }
      
      // Steps that we advance *manually* (not via Joyride "Next") - check BEFORE advancing
      // But only prevent if we're NOT manually advancing to this step
      if (action === 'next' && manuallyAdvancingToRef.current !== nextIndex && (
        currentStep?.id === 'set-roles' ||         // step 2
        currentStep?.id === 'choose-chart-type' || // step 3
        currentStep?.id === 'send-message' ||      // step 4
        currentStep?.id === 'review-plan'          // step 5
      )) {
        console.log(
          '[TourManager] Step',
          index,
          `(${currentStep.id}) trying to auto-advance, preventing - manual advancement only`
        );
        // Re-assert the current step index so Joyride doesn't move on
        requestAnimationFrame(() => {
          setCurrentStepIndex(index);
        });
        return;
      }
      
      const nextStep = onboardingSteps[nextIndex];

      setCurrentStepIndex(nextIndex);
      const newState: TourState = { status: 'in_progress', step: nextIndex, tourId: 'onboarding' };
      setState(newState);
      saveTourState(newState);
      trackOnboardingEvent('onboarding:step_shown', { step_id: nextStep?.id });
      console.log('[TourManager] Step completed via click, advancing');
    }

    // Handle error:target_not_found - prevent react-joyride from skipping steps
    if (type === 'error:target_not_found') {
      // Try to get the step ID from the callback data first (most reliable)
      const callbackStepId = data.step && typeof data.step === 'object' ? (data.step as any)?.id : undefined;
      
      // Fall back to checking index and index - 1 if step ID not available
      const stepAtIndex = index >= 0 && index < onboardingSteps.length ? onboardingSteps[index] : null;
      const stepAtPrevIndex = index > 0 && index - 1 < onboardingSteps.length ? onboardingSteps[index - 1] : null;
      
      // Resolve the actual failing step:
      // 1. Try to find step by callback step ID (most reliable)
      // 2. Fall back to step at reported index
      // 3. Only use index - 1 if step at index can't be resolved
      const resolvedStep = callbackStepId
        ? onboardingSteps.find((s) => s.id === callbackStepId) || stepAtIndex || stepAtPrevIndex
        : stepAtIndex || stepAtPrevIndex;
      
      const resolvedIndex = resolvedStep ? onboardingSteps.findIndex((s) => s.id === resolvedStep.id) : -1;
      
      console.log(
        '[TourManager] error:target_not_found handler',
        { 
          callbackIndex: index,
          callbackStepId,
          resolvedIndex,
          resolvedStepId: resolvedStep?.id,
          stepAtIndexId: stepAtIndex?.id,
          stepAtPrevIndexId: stepAtPrevIndex?.id
        }
      );
      
      if (resolvedStep?.id === 'set-roles' || resolvedStep?.id === 'choose-chart-type' || resolvedStep?.id === 'send-message') {
        console.log(
          '[TourManager] Joyride reported target not found for step', index + 1, '- keeping tour running; watcher will fix target'
        );
        // Keep run=true; just keep stepIndex pinned so Joyride keeps trying this step.
        requestAnimationFrame(() => {
          setCurrentStepIndex(resolvedIndex >= 0 ? resolvedIndex : index);
        });
        return;
      }
      
      if (resolvedStep?.id === 'review-plan') {
        console.log(
          '[TourManager] Joyride reported target not found for step 4 (review-plan) - waiting for plan-ready event'
        );
        if (isRunningRef.current) setIsRunning(false);
        // Keep the step index pinned to review-plan
        requestAnimationFrame(() => {
          setCurrentStepIndex(REVIEW_PLAN_INDEX);
        });
        // 🚀 CRITICAL: attach the plan watcher here
        waitForPlanCard();
        return;
      }
      
      console.log(
        '[TourManager] Joyride target not found for step',
        index,
        `(stepId: ${resolvedStep?.id || 'unknown'}, resolvedIndex: ${resolvedIndex})`,
        '- letting Joyride advance/finish'
      );
    }

    
   // Monitor tooltip appearance for step 1 and step 2
   if (type === 'tooltip') {
     const currentStep = onboardingSteps[index];
     
     // Step 2 = index 1 (choose-chart-type)
     if (index === 1 && currentStep?.id === 'choose-chart-type') {
       console.log('[TourManager] Step', index, 'tooltip shown');
       // Don't aggressively correct - let the step:after handler manage advancement
       // The step:after handler already prevents auto-advancement for this step
     }
     
     // Manual-advance steps (set-roles, choose-chart-type, send-message) - keep tour running so tooltip stays visible
     if ((index === SET_ROLES_INDEX || index === CHOOSE_CHART_INDEX || index === SEND_MESSAGE_INDEX) && 
         (currentStep?.id === 'set-roles' || currentStep?.id === 'choose-chart-type' || currentStep?.id === 'send-message')) {
       console.log(
         '[TourManager] Step', index + 1, 'tooltip shown - leaving tour running so user can interact'
       );
       // ✅ DO NOT touch isRunning here - keep tour running so tooltip stays visible
       // The step:after handler already prevents auto-advancement for this step
       
       // Signal that tooltip is ready - this will coordinate with highlight timing
       (window as any).__lumiStep3TooltipReady = true;
       
       // Scroll chat to bottom to show the encodings panel
       setTimeout(() => {
         try {
           const chatScrollArea = document.querySelector('[data-testid="chat-messages"]');
           if (chatScrollArea) {
             const scrollViewport = chatScrollArea.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
             if (scrollViewport) {
               scrollViewport.scrollTop = scrollViewport.scrollHeight;
               console.log('[TourManager] Scrolled chat to bottom when step', index + 1, 'tooltip appeared');
             }
           }
         } catch (err) {
           console.warn('[TourManager] Failed to scroll chat when step', index + 1, 'tooltip appeared', err);
         }
       }, 100);
     }
   }

    if (action === 'close') {
      // For manual-advance steps, don't dismiss - keep tooltip visible
      // User needs to be able to interact while seeing the tooltip
      const currentStep = onboardingSteps[index];
      if ((currentStep?.id === 'set-roles' || currentStep?.id === 'choose-chart-type' || currentStep?.id === 'send-message') && 
          (index === SET_ROLES_INDEX || index === CHOOSE_CHART_INDEX || index === SEND_MESSAGE_INDEX)) {
        console.log('[TourManager] User clicked outside step', index + 1, '- keeping tooltip visible, allowing interaction');
        // Don't pause - keep run=true so tooltip stays visible
        // Don't dismiss - keep tour state as 'in_progress'
        // The tour will advance to step 4 when user sends message
        // The tooltip will remain visible because run stays true
        return;
      }

      // 🔐 If we're waiting for the final step after Approve & Run,
      // treat this "close" as an internal pause, not a user dismissal.
      if (
        awaitingFinalStepRef.current ||
        pauseReasonRef.current === 'waiting_for_final_step'
      ) {
        console.log(
          '[TourManager] Close action ignored - waiting for final step (waiting_for_final_step)',
          { pauseReason: pauseReasonRef.current, awaitingFinalStep: awaitingFinalStepRef.current }
        );
        return;
      }

      // Existing generic "paused" guard (waiting_for_plan, step3_to_step4, auto_start, etc.)
      if (pauseReasonRef.current !== null) {
        console.log(
          '[TourManager] Close action ignored - tour is paused for internal reason:',
          pauseReasonRef.current
        );
        return;
      }

      // Check if this is Step 0 auto-start (index 0, upload-data step)
      if (index === 0 && currentStep?.id === 'upload-data') {
        console.log('[TourManager] Close action on Step 0 ignored - auto-start pause');
        // Set pause reason for auto-start to prevent dismissal
        pauseReasonRef.current = 'auto_start';
        // Clear it after a short delay to allow normal dismissal later
        setTimeout(() => {
          pauseReasonRef.current = null;
        }, 1000);
        return;
      }

      // 🔑 CRITICAL: For Step 4 (review-plan), don't dismiss the tour when X is clicked
      // The dismissTooltip() function already handles hiding the tooltip
      // We need to keep the tour active so Step 5 can show when plot is built
      if (index === REVIEW_PLAN_INDEX && currentStep?.id === 'review-plan') {
        console.log('[TourManager] Close action on Step 4 - only hiding tooltip, keeping tour active for Step 5');
        // Just hide the tooltip, but don't dismiss the tour
        // The tour will continue to Step 5 when plot is built
        setIsRunning(false);
        return;
      }

      // For other steps, dismiss normally (user explicitly closed)
      dismiss();
    }
  }, [dismiss, currentStepIndex, isRunning, projectId, onboardingSteps, setCurrentSteps, setCurrentStepIndex, setState, saveTourState, trackOnboardingEvent, waitForPlanCard, REVIEW_PLAN_INDEX, clearFinalStepWaitingState, setIsRunning]);

  /**
   * Show post-save CTA steps sequentially
   */
  const showPostSaveCTAs = useCallback(() => {
    // Early return if no post-save CTA steps are defined
    if (postSaveCTASteps.length === 0) {
      console.log('[TourManager] Post-save CTAs disabled - no steps defined');
      return;
    }
    
    // 🔑 CRITICAL: Don't show post-save CTAs if we're waiting for step 5 (final step)
    // Step 5 should show first, then post-save CTAs can show after step 5 is completed
    const storedState = loadTourState();
    const isWaitingForStep5 = awaitingFinalStepRef.current || 
                               pauseReasonRef.current === 'waiting_for_final_step' ||
                               (storedState.status === 'in_progress' && storedState.step === SAVE_AND_ACTIONS_INDEX);
    
    if (isWaitingForStep5) {
      console.log('[TourManager] Skipping post-save CTAs - waiting for step 5 (final step) to show first', {
        awaitingFinalStep: awaitingFinalStepRef.current,
        pauseReason: pauseReasonRef.current,
        storedStep: storedState.step,
        storedStatus: storedState.status
      });
      return;
    }
    
    console.log('[TourManager] Showing post-save CTAs');
    const wasRunning = isRunning;
    setIsPostSaveMode(true);
    setIsRunning(false); // Pause main tour

    const ctaSteps = postSaveCTASteps.map((step, idx) =>
      convertTourStepToJoyride(step, idx, projectId, undefined, undefined, undefined, undefined, skip, dismissTooltip, false)
    );

    // Wait for CTA targets to be ready
    const waitForCTATargets = () => {
      return new Promise<void>((resolve) => {
        let attempts = 0;
        const maxAttempts = 50; // 10 seconds

        const checkInterval = setInterval(() => {
          attempts++;
          const allTargetsReady = postSaveCTASteps.every(step => {
            const el = document.querySelector(step.target);
            return !!el;
          });

          if (allTargetsReady) {
            clearInterval(checkInterval);
            resolve();
          } else if (attempts >= maxAttempts) {
            console.warn('[TourManager] Timeout waiting for CTA targets, restoring main tour');
            clearInterval(checkInterval);
            // Restore main tour if CTAs not ready
            if (wasRunning) {
              setIsPostSaveMode(false);
              setIsRunning(true);
            }
            resolve();
          }
        }, 200);
      });
    };

    waitForCTATargets().then(() => {
      if (isPostSaveMode) {
        setCurrentSteps(ctaSteps);
        setCurrentStepIndex(0);
        setIsRunning(true);
      }
    });
  }, [isRunning, isPostSaveMode, projectId, SAVE_AND_ACTIONS_INDEX, setCurrentSteps, setCurrentStepIndex, setIsRunning, skip, dismissTooltip]);

  // Listen for save success event
  useEffect(() => {
    const handleSaveSuccess = () => {
      console.log('[TourManager] Save success event received');
      showPostSaveCTAs();
    };

    window.addEventListener('lumiplot-save-success', handleSaveSuccess);
    return () => {
      window.removeEventListener('lumiplot-save-success', handleSaveSuccess);
    };
  }, [showPostSaveCTAs]);

  // Listen for start-tour event from header menu
  useEffect(() => {
    const handleStartTourEvent = (event: CustomEvent<{ id: string }>) => {
      const tourId = event.detail?.id;
      if (tourId === 'onboarding') {
        console.log('[TourManager] Start tour event received from header');
        startTour('onboarding', true); // Force start
      }
    };

    window.addEventListener('lumi:start-tour', handleStartTourEvent as EventListener);
    return () => {
      window.removeEventListener('lumi:start-tour', handleStartTourEvent as EventListener);
    };
  }, [startTour]);

  // Listen for tour advance step event (fallback when onAdvanceStep is not available)
  useEffect(() => {
    const handleAdvanceStepEvent = (event: CustomEvent<{ stepIndex: number }>) => {
      const stepIndex = event.detail?.stepIndex;
      if (typeof stepIndex === 'number' && stepIndex >= 0) {
        console.log('[TourManager] Tour advance step event received, advancing to step', stepIndex);
        handleAdvanceStep(stepIndex);
      }
    };

    window.addEventListener('lumi:tour-advance-step', handleAdvanceStepEvent as EventListener);
    return () => {
      window.removeEventListener('lumi:tour-advance-step', handleAdvanceStepEvent as EventListener);
    };
  }, [handleAdvanceStep]);

  // Highlight grouped bars chart type when choose-chart-type step is active
  // Advance tour when grouped bars chart is selected
  // Uses the same waitAndHighlight pattern as steps 1 and 2
  useEffect(() => {
    const currentStep = onboardingSteps[currentStepIndex];
    const isGroupedBarStep = currentStep?.id === 'choose-chart-type';
    
    if (!isRunning || !isGroupedBarStep) {
      // Remove highlight class when not on grouped bar step
      const element = document.querySelector('[data-lumi="charttype-bar_vertical_grouped"]');
      if (element) {
        element.classList.remove('tour-highlight-active');
        const clickHandler = (element as any).__tourClickHandler;
        if (clickHandler) {
          element.removeEventListener('click', clickHandler);
          delete (element as any).__tourClickHandler;
        }
      }
      // Remove highlight from role-x when leaving step 2
      const roleX = document.querySelector('[data-lumi="role-x"]');
      if (roleX) {
        roleX.classList.remove('tour-highlight-active');
      }
      return;
    }
    
    // Remove highlight from role-x when entering step 3 (choose-chart-type)
    const roleX = document.querySelector('[data-lumi="role-x"]');
    if (roleX) {
      roleX.classList.remove('tour-highlight-active');
    }

    // Grouped bar step is active - use waitAndHighlight pattern similar to steps 1 and 2
    const waitAndHighlight = () => {
      let attempts = 0;
      const maxAttempts = 50; // 10 seconds max wait
      
      const checkElement = () => {
        // Check if we're still on the choose-chart-type step
        const currentStepCheck = onboardingSteps[currentStepIndex];
        if (!isRunning || currentStepCheck?.id !== 'choose-chart-type') {
          return;
        }
        
        attempts++;
        
        const element = document.querySelector('[data-lumi="charttype-bar_vertical_grouped"]');
        const chartTypeShelves = document.querySelector('[data-lumi="chart-type-shelves"]');
        
        // Check if chart type shelves are visible
        const shelvesVisible = chartTypeShelves ? (chartTypeShelves as HTMLElement).offsetParent !== null : false;
        
        console.log('[TourManager] Checking for grouped bars chart (attempt', attempts, '):', {
          elementFound: !!element,
          shelvesVisible,
        });

        if (element && shelvesVisible) {
          console.log('[TourManager] Grouped bars chart found and visible, waiting for tooltip before highlighting');
          
          // Wait for tooltip to be ready, then add highlight with coordinated delay
          const applyHighlight = () => {
            // Double-check we're still on choose-chart-type step
            const currentStepCheck = onboardingSteps[currentStepIndex];
            if (!isRunning || currentStepCheck?.id !== 'choose-chart-type') {
              return;
            }
            
            const element = document.querySelector('[data-lumi="charttype-bar_vertical_grouped"]');
            if (!element) {
              return;
            }
            
            console.log('[TourManager] Applying highlight to grouped bars chart - tooltip and element ready');
            
            // Add highlight class
            if (!element.classList.contains('tour-highlight-active')) {
              element.classList.add('tour-highlight-active');
            }
            
            // Add click handler to advance tour when grouped bar is clicked
            if (!(element as any).__tourClickHandler) {
              const clickHandler = () => {
                // Only advance if we're still on choose-chart-type step
                if (currentStepIndexRef.current === CHOOSE_CHART_INDEX && isRunningRef.current) {
                  console.log('[TourManager] Grouped bars chart clicked, advancing to send step');
                  
                  // Stop Joyride temporarily
                  setIsRunning(false);
                  
                  // Remove highlight
                  element.classList.remove('tour-highlight-active');
                  
                  // Advance to send-message step using handleAdvanceStep
                  const sendStepIndex = SEND_MESSAGE_INDEX;
                  if (sendStepIndex >= 0) {
                    handleAdvanceStep(sendStepIndex);
                  }
                }
              };
              
              (element as any).__tourClickHandler = clickHandler;
              element.addEventListener('click', clickHandler, { once: false });
            }
          };
          
          // Wait for tooltip to be ready, then apply highlight with a coordinated delay
          let tooltipCheckAttempts = 0;
          const maxTooltipChecks = 30; // 3 seconds max wait for tooltip
          
          const checkTooltipReady = () => {
            tooltipCheckAttempts++;
            const tooltipReady = !!(window as any).__lumiStep3TooltipReady || document.querySelector('[role="dialog"][id*="tour-step"]');
            
            if (tooltipReady || tooltipCheckAttempts >= maxTooltipChecks) {
              console.log('[TourManager] Tooltip confirmed ready, applying highlight in 400ms');
              setTimeout(() => {
                applyHighlight();
              }, 400);
            } else {
              setTimeout(checkTooltipReady, 100);
            }
          };
          
          checkTooltipReady();
          
          return; // Stop polling
        }
        
        if (attempts >= maxAttempts) {
          console.warn('[TourManager] Grouped bars chart not found after max attempts');
          return;
        }
        
        // Continue polling
        setTimeout(checkElement, 200);
      };
      
      checkElement();
    };

    // Start the waitAndHighlight process
    waitAndHighlight();

    return () => {
      const element = document.querySelector('[data-lumi="charttype-bar_vertical_grouped"]');
      if (element) {
        element.classList.remove('tour-highlight-active');
        const clickHandler = (element as any).__tourClickHandler;
        if (clickHandler) {
          element.removeEventListener('click', clickHandler);
          delete (element as any).__tourClickHandler;
        }
      }
    };
  }, [isRunning, currentStepIndex, projectId, CHOOSE_CHART_INDEX, SEND_MESSAGE_INDEX, onboardingSteps, setCurrentSteps, setCurrentStepIndex, setState, saveTourState, skip, dismissTooltip, handleAdvanceStep]);

  // Listen for message sent event to advance from send step to review-plan step
  useEffect(() => {
    const currentStep = onboardingSteps[currentStepIndex];
    const isSendStep = currentStep?.id === 'send-message';
    
    // Remove highlight from role-x when entering step 4 (send-message) or after
    if (isSendStep || currentStep?.id === 'review-plan' || currentStep?.id === 'save-and-actions') {
      const roleX = document.querySelector('[data-lumi="role-x"]');
      if (roleX) {
        roleX.classList.remove('tour-highlight-active');
      }
    }
    
    if (!isRunning || !isSendStep) {
      return;
    }

    const handleMessageSent = () => {
      if (currentStepIndexRef.current === SEND_MESSAGE_INDEX && isRunningRef.current) {
        console.log('[TourManager] Message sent, advancing to review-plan step');
        
        // Remove highlight from role-x after sending
        const roleX = document.querySelector('[data-lumi="role-x"]');
        if (roleX) {
          roleX.classList.remove('tour-highlight-active');
        }
        
        // Stop Joyride temporarily
        setIsRunning(false);
        
        // Advance to review-plan step
        const reviewPlanIndex = REVIEW_PLAN_INDEX;
        if (reviewPlanIndex >= 0) {
                const updatedSteps = onboardingSteps.map((s, i) =>
                  convertTourStepToJoyride(s, i, projectId, undefined, undefined, undefined, handleAdvanceStep, skip, dismissTooltip, true)
                );
          setCurrentSteps(updatedSteps);
          setCurrentStepIndex(reviewPlanIndex);
          const newState: TourState = { status: 'in_progress', step: reviewPlanIndex, tourId: 'onboarding' };
          setState(newState);
          saveTourState(newState);
          
          // Wait for plan card to appear (existing waitForPlanCard logic will handle this)
          waitForPlanCard();
        }
      }
    };

    // Listen for custom event when message is sent
    window.addEventListener('lumi:message-sent', handleMessageSent);
    
    return () => {
      window.removeEventListener('lumi:message-sent', handleMessageSent);
    };
  }, [isRunning, currentStepIndex, SEND_MESSAGE_INDEX, REVIEW_PLAN_INDEX, projectId, onboardingSteps, setCurrentSteps, setCurrentStepIndex, setState, saveTourState, waitForPlanCard, skip, dismissTooltip]);

  /**
   * Move tour from Step 1/2 (set-roles/choose-chart-type) → Step 3 (review-plan)
   * - Hides step 3 tooltip (run = false)
   * - Persists state as step 4
   * - Starts the plan-card watcher, which will restart Joyride when ready
   */
  const advanceFromEncodingsToReviewPlan = useCallback(() => {
    if (SET_ROLES_INDEX < 0 || CHOOSE_CHART_INDEX < 0 || REVIEW_PLAN_INDEX < 0) {
      console.warn('[TourManager] Cannot advance: step indices not found');
      return;
    }

    const storedState = loadTourState();

    if (currentStepIndexRef.current !== SET_ROLES_INDEX && currentStepIndexRef.current !== CHOOSE_CHART_INDEX) {
      console.log(
        '[TourManager] advanceFromEncodingsToReviewPlan: ignoring send (not at step 1 or 2)',
        { runtimeStep: currentStepIndexRef.current, storedStep: storedState.step }
      );
      return;
    }

    console.log(
      '[TourManager] advanceFromEncodingsToReviewPlan: invoked, moving tour to Step 4 (review-plan)'
    );

    // Clear refs for new run - allow Step 4 watcher to reattach cleanly
    waitingForPlanCardRef.current = false;
    stopWaitingForPlanCard(); // Clean up any existing watchers

    const newState: TourState = {
      status: 'in_progress',
      step: REVIEW_PLAN_INDEX,
      tourId: 'onboarding',
      snoozeUntil: storedState.snoozeUntil,
    };

    setState(newState);
    saveTourState(newState);

    setCurrentStepIndex(REVIEW_PLAN_INDEX);
    
    // Set pause reason for Step 3→4 handoff BEFORE hiding tooltip
    // This ensures the pause reason is set before Joyride fires status:finished
    pauseReasonRef.current = 'step3_to_step4';
    
    setIsRunning(false); // 🔑 hides step 3 tooltip immediately

    // Note: encodings-panel highlight is now handled by ChartBuilderCard via React prop, no DOM manipulation needed
    document
      .querySelector('[data-lumi="chat-send"]')
      ?.classList.remove('tour-highlight-active');

    waitForPlanCard(); // start listening for lumiplot-plan-ready event to restart Joyride at step 4
  }, [SET_ROLES_INDEX, CHOOSE_CHART_INDEX, REVIEW_PLAN_INDEX, waitForPlanCard, stopWaitingForPlanCard]);

  /**
   * Show final step tooltip (save-and-actions)
   * Waits for save button to appear, then shows the tooltip
   * @param source - Source of the trigger ('plot_built' | 'timeout')
   */
  const showFinalStepTooltip = useCallback((source: 'plot_built' | 'timeout') => {
    if (SAVE_AND_ACTIONS_INDEX < 0) {
      console.warn('[TourManager] showFinalStepTooltip: SAVE_AND_ACTIONS_INDEX not found');
      return;
    }

    const storedState = loadTourState();

    // Wait for save button to appear before showing final step
    const waitForSaveButton = () => {
      const saveButton = document.querySelector('[data-lumi="btn-save"]');
      if (saveButton) {
        console.log(`[TourManager] Save button found, showing final step tooltip (source: ${source})`);
        
        // Update steps array
                const updatedSteps = onboardingSteps.map((s, i) =>
                  convertTourStepToJoyride(s, i, projectId, undefined, undefined, undefined, handleAdvanceStep, skip, dismissTooltip, true)
                );
        setCurrentSteps(updatedSteps);
        
        // Move to final step
        const newState: TourState = {
          status: 'in_progress',
          step: SAVE_AND_ACTIONS_INDEX,
          tourId: 'onboarding',
          snoozeUntil: storedState.snoozeUntil,
        };
        setState(newState);
        saveTourState(newState);
        setCurrentStepIndex(SAVE_AND_ACTIONS_INDEX);
        // Clear waiting state (includes timeout and pause reason cleanup) right before restarting Joyride
        clearFinalStepWaitingState();
        // 🔑 CRITICAL: Clear post-save mode if it's active, since step 5 takes priority
        if (isPostSaveMode) {
          console.log('[TourManager] Clearing post-save mode to show step 5 (final step)');
          setIsPostSaveMode(false);
        }
        setIsRunning(true);
        
        trackOnboardingEvent('onboarding:step_shown', {
          step_id: 'save-and-actions',
          source: source === 'plot_built' ? 'post_approval' : 'post_approval_timeout'
        });
        console.log(`[TourManager] Final step (save-and-actions) tooltip activated (source: ${source})`);
        return;
      }

      // If not found, wait for it to appear
      const observer = new MutationObserver(() => {
        const saveButton = document.querySelector('[data-lumi="btn-save"]');
        if (saveButton) {
          observer.disconnect();
          waitForSaveButton();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Safety timeout: stop after 10 seconds (shouldn't happen if save button is ready)
      setTimeout(() => {
        observer.disconnect();
        console.warn('[TourManager] Timeout waiting for save button in showFinalStepTooltip');
      }, 10000);
    };

    // Start waiting for save button
    waitForSaveButton();
  }, [SAVE_AND_ACTIONS_INDEX, onboardingSteps, projectId, setCurrentSteps, setState, setCurrentStepIndex, setIsRunning, clearFinalStepWaitingState, isPostSaveMode, setIsPostSaveMode]);

  /**
   * Move tour from Step 4 (review-plan) → final step (save-and-actions)
   * - Hides Step 4 tooltip immediately
   * - Sets up waiting state for artifacts
   * - Final step tooltip will be shown when analysis:plot_built event fires
   */
  const advanceFromPlanToFinalStep = useCallback(() => {
    if (REVIEW_PLAN_INDEX < 0 || SAVE_AND_ACTIONS_INDEX < 0) {
      console.warn(
        '[TourManager] advanceFromPlanToFinalStep: step indices not found',
        { REVIEW_PLAN_INDEX, SAVE_AND_ACTIONS_INDEX }
      );
      return;
    }

    // 🔑 CRITICAL: Allow calling even if currentStepIndex doesn't match exactly
    // This handles cases where tooltip was closed but we're still on step 4 conceptually
    const currentStoredState = loadTourState();
    const isOnStep4 = currentStepIndexRef.current === REVIEW_PLAN_INDEX || 
                      currentStoredState.step === REVIEW_PLAN_INDEX ||
                      currentStoredState.step === SAVE_AND_ACTIONS_INDEX; // Also allow if already waiting for final step
    
    if (!isOnStep4) {
      console.log(
        '[TourManager] advanceFromPlanToFinalStep called but not on step 4',
        { currentStep: currentStepIndexRef.current, storedStep: currentStoredState.step }
      );
      return;
    }
    
    // If tour was dismissed, restore it to in_progress
    if (currentStoredState.status === 'dismissed') {
      console.log('[TourManager] Tour was dismissed, restoring to in_progress for Step 5');
      const restoredState: TourState = {
        status: 'in_progress',
        step: REVIEW_PLAN_INDEX,
        tourId: 'onboarding',
        snoozeUntil: currentStoredState.snoozeUntil,
      };
      setState(restoredState);
      saveTourState(restoredState);
    }

    console.log('[TourManager] Step 4 (review-plan) approval clicked - hiding tooltip and waiting for artifacts');
    console.log('[TourManager] Telemetry: Step 4 completed via approve click');

    // Clear any existing timeout (legacy cleanup, timeout removed in favor of pure event-driven approach)
    if (finalStepTimeoutRef.current) {
      clearTimeout(finalStepTimeoutRef.current);
      finalStepTimeoutRef.current = null;
    }

    // Stop plan card watcher
    stopWaitingForPlanCard();

    // Set waiting state and pause reason BEFORE hiding tooltip to prevent dismissal
    awaitingFinalStepRef.current = true;
    pauseReasonRef.current = 'waiting_for_final_step';

    // 🔑 CRITICAL: Persist explicit "waiting-for-final-step" marker in localStorage
    // This prevents the hydration effect from restarting Step 4 watchers
    // We persist the step as SAVE_AND_ACTIONS_INDEX to indicate we're waiting for final step
    const storedState = loadTourState();
    const waitingState: TourState = {
      status: 'in_progress',
      step: SAVE_AND_ACTIONS_INDEX, // Use final step index as marker
      tourId: 'onboarding',
      snoozeUntil: storedState.snoozeUntil,
    };
    setState(waitingState);
    saveTourState(waitingState);

    // Hide Step 4 tooltip immediately
    setIsRunning(false);

    // 🔑 REMOVED: Timeout fallback removed in favor of pure event-driven approach.
    // Step 5 will only show when analysis:plot_built event fires from lumi-chat-v2.
    // If no event arrives, the tooltip stays hidden (event-only requirement).
  }, [REVIEW_PLAN_INDEX, SAVE_AND_ACTIONS_INDEX, stopWaitingForPlanCard, setIsRunning, showFinalStepTooltip, clearFinalStepWaitingState, setState, saveTourState]);

  // Listen for analysis:plot_built event to show final step when artifacts are ready
  useEffect(() => {
    const handlePlotBuilt = (event: Event) => {
      const customEvent = event as CustomEvent<{ projectId?: string; plotId?: string; planId?: string; run_id?: string }>;
      const eventProjectId = customEvent.detail?.projectId;
      const eventPlotId = customEvent.detail?.plotId;
      const eventPlanId = customEvent.detail?.planId;
      const eventRunId = customEvent.detail?.run_id;

      // Check if we're waiting for final step
      if (!awaitingFinalStepRef.current) {
        // 🔑 CRITICAL: Telemetry logging when event arrives after refs were cleared
        // This helps catch regressions where the flag was cleared prematurely or event arrived late
        const storedState = loadTourState();
        const telemetryData = {
          pauseReason: pauseReasonRef.current,
          storedStep: storedState.step,
          storedStatus: storedState.status,
          awaitingFinalStep: awaitingFinalStepRef.current,
          eventProjectId,
          eventPlotId,
          eventPlanId,
          eventRunId,
          currentProjectId: projectId,
        };
        
        if (storedState.status === 'in_progress' && storedState.step === SAVE_AND_ACTIONS_INDEX) {
          console.warn(
            '[TourManager] analysis:plot_built event received but awaitingFinalStepRef is false - flag may have been cleared prematurely',
            telemetryData
          );
          // Log telemetry for regression tracking
          console.warn('[TourManager] Telemetry: Event arrived after refs cleared - potential race condition or premature cleanup', telemetryData);
        } else {
          // Event arrived but we're not in waiting state (normal case - tour completed or not active)
          console.log('[TourManager] analysis:plot_built event received but not waiting for final step (normal)', telemetryData);
        }
        return;
      }

      // Verify event belongs to current project
      if (eventProjectId && projectId && eventProjectId !== projectId) {
        console.log(
          '[TourManager] analysis:plot_built event ignored - projectId mismatch',
          { eventProjectId, currentProjectId: projectId, eventPlotId, eventPlanId }
        );
        return;
      }

      console.log('[TourManager] analysis:plot_built event received, showing final step tooltip');
      console.log('[TourManager] Telemetry: Artifacts ready, activating final step', {
        eventProjectId,
        eventPlotId,
        eventPlanId,
        eventRunId,
        currentProjectId: projectId,
      });

      // Clear waiting state (includes timeout and pause reason cleanup)
      clearFinalStepWaitingState();

      // Show final step tooltip
      showFinalStepTooltip('plot_built');
    };

    window.addEventListener('analysis:plot_built', handlePlotBuilt);
    return () => {
      window.removeEventListener('analysis:plot_built', handlePlotBuilt);
    };
  }, [projectId, showFinalStepTooltip, clearFinalStepWaitingState, SAVE_AND_ACTIONS_INDEX]);

  // Add custom highlight class to send button when Step 1 or 2 (set-roles or choose-chart-type) is active
  // Also wait for encodings panel and handle clicks on the send button to advance the tour
  useEffect(() => {
    const currentStep = onboardingSteps[currentStepIndex];
    const isEncodingsStep = currentStep?.id === 'set-roles' || currentStep?.id === 'choose-chart-type';
    const isOnRolesOrChartStep = currentStepIndex === SET_ROLES_INDEX || currentStepIndex === CHOOSE_CHART_INDEX;
    const shouldHighlight = isRunning && isEncodingsStep;
    
    // 🔑 CRITICAL: Only remove highlights if we're actually leaving step 1 or 2
    // Don't remove on transient isRunning=false while still on these steps (Joyride pause events)
    const isLeavingRolesOrChartStep = !isOnRolesOrChartStep;
    
    if (isLeavingRolesOrChartStep && step3HighlightedRef.current) {
      // Clear any pending intervals
      if (step3HighlightIntervalRef.current) {
        clearTimeout(step3HighlightIntervalRef.current);
        step3HighlightIntervalRef.current = null;
      }
      step3HighlightedRef.current = false;
      // Clear tooltip ready flag
      (window as any).__lumiStep3TooltipReady = false;
      // Remove highlight class when leaving encodings step
      const sendButton = document.querySelector('[data-lumi="chat-send"]');
      if (sendButton) {
        sendButton.classList.remove('tour-highlight-active');
        const clickHandler = (sendButton as any).__tourClickHandler;
        if (clickHandler) {
          sendButton.removeEventListener('click', clickHandler);
          delete (sendButton as any).__tourClickHandler;
        }
      }
      // Note: encodings-panel highlight is now handled by ChartBuilderCard via React prop, no DOM manipulation needed
      return;
    }
    
    // 🔑 CRITICAL: Effect must run when on step 1 or 2 (even if paused) to attach send handler
    // But only apply highlights when isRunning is true
    if (!isOnRolesOrChartStep) {
      return;
    }

    // 🔑 helper used by both click + Enter
    const onSend = () => {
      advanceFromEncodingsToReviewPlan();
    };

    // Encodings step is active - wait for panel to be fully unfolded, then add highlights and show tooltip
    // The Roles panel is a Collapsible that needs to be opened first
    const waitAndHighlight = () => {
      // Don't start if already highlighted
      if (step3HighlightedRef.current) {
        console.log('[TourManager] Highlight already applied, skipping');
        return;
      }
      
      let attempts = 0;
      const maxAttempts = 50; // 10 seconds max wait
      
      const checkPanel = () => {
        // Check if we're still on the encodings step
        const currentStepCheck = onboardingSteps[currentStepIndex];
        if (!isRunning || (currentStepCheck?.id !== 'set-roles' && currentStepCheck?.id !== 'choose-chart-type') || step3HighlightedRef.current) {
          if (step3HighlightIntervalRef.current) {
            clearTimeout(step3HighlightIntervalRef.current);
            step3HighlightIntervalRef.current = null;
          }
          return;
        }
        
        attempts++;
        
        // Check if Roles section is visible (CollapsibleContent is expanded)
        const allElements = Array.from(document.querySelectorAll('*'));
        const rolesSection = allElements.find(el => {
          const text = el.textContent || '';
          if (!text.includes('Roles') || !text.includes('All roles are optional')) {
            return false;
          }
          // Check if element is visible (HTMLElement has offsetParent)
          const htmlEl = el as HTMLElement;
          return htmlEl.offsetParent !== null; // Element is visible
        });
        
        // Also check if CollapsibleContent is actually visible (not hidden)
        const collapsibleContent = document.querySelector('[data-lumi="roles-panel"] [data-state="open"]');
        const rolesSectionVisible = rolesSection ? (rolesSection as HTMLElement).offsetParent !== null : false;
        const isPanelUnfolded = !!collapsibleContent || rolesSectionVisible;
        
        console.log('[TourManager] Checking for roles panel (attempt', attempts, '):', {
          rolesSectionFound: !!rolesSection,
          rolesSectionVisible,
          collapsibleOpen: !!collapsibleContent,
          isPanelUnfolded,
        });

        if (isPanelUnfolded) {
          console.log('[TourManager] Encodings panel found and unfolded, waiting for tooltip before highlighting');
          
          // Stop polling - we found the panel
          if (step3HighlightIntervalRef.current) {
            clearTimeout(step3HighlightIntervalRef.current);
            step3HighlightIntervalRef.current = null;
          }
          
          // Wait for tooltip to be ready, then add highlight with coordinated delay
          const applyHighlight = () => {
            // Double-check we're still on step 1 or 2
            const currentStepCheck = onboardingSteps[currentStepIndex];
            if (!isRunning || (currentStepCheck?.id !== 'set-roles' && currentStepCheck?.id !== 'choose-chart-type') || step3HighlightedRef.current) {
              return;
            }
            
            console.log('[TourManager] Applying highlight - tooltip and panel ready');
            // Note: Don't set step3HighlightedRef here - it will be set after confirming classes were applied
            
            // Scroll chat to bottom to show the encodings panel
            const scrollToChatBottom = () => {
              try {
                // Find the chat messages scroll container - ScrollArea from Radix UI
                const chatScrollArea = document.querySelector('[data-testid="chat-messages"]');
                if (chatScrollArea) {
                  // Try to find the ScrollArea viewport (Radix UI structure)
                  const scrollViewport = chatScrollArea.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
                  if (scrollViewport) {
                    scrollViewport.scrollTop = scrollViewport.scrollHeight;
                    console.log('[TourManager] Scrolled ScrollArea viewport to bottom for step', currentStepIndex + 1);
                    return;
                  }
                  // Fallback: try to find any scrollable child element
                  const scrollableChild = Array.from(chatScrollArea.querySelectorAll('*')).find((el) => {
                    const htmlEl = el as HTMLElement;
                    const style = window.getComputedStyle(htmlEl);
                    return style.overflowY === 'auto' || style.overflowY === 'scroll' || style.overflow === 'auto' || style.overflow === 'scroll';
                  }) as HTMLElement;
                  if (scrollableChild) {
                    scrollableChild.scrollTop = scrollableChild.scrollHeight;
                    console.log('[TourManager] Scrolled scrollable child to bottom for step 3');
                    return;
                  }
                  // Last resort: try scrolling the ScrollArea itself
                  const htmlEl = chatScrollArea as HTMLElement;
                  if (htmlEl.scrollHeight > htmlEl.clientHeight) {
                    htmlEl.scrollTop = htmlEl.scrollHeight;
                    console.log('[TourManager] Scrolled ScrollArea element to bottom for step 3');
                  }
                }
              } catch (err) {
                console.warn('[TourManager] Failed to scroll chat to bottom', err);
              }
            };
            
            // Scroll immediately
            scrollToChatBottom();
            
            // Note: encodings-panel highlight is now handled by ChartBuilderCard via React prop (tourHighlightActive)
            // No DOM manipulation needed here - ChartBuilderCard will re-render with the class
            
            // Panel is ready and unfolded, now highlight send button
            // Note: Send button click handler is attached in main effect, no need to attach here
            const sendButton = document.querySelector('[data-lumi="chat-send"]');
            if (sendButton && !sendButton.classList.contains('tour-highlight-active')) {
              sendButton.classList.add('tour-highlight-active');
              
              // 🔑 CRITICAL: Only set ref to true AFTER confirming classes were successfully applied
              // This ensures recovery can happen if highlight application actually failed
              step3HighlightedRef.current = true;
              console.log('[TourManager] Step', currentStepIndex + 1, 'highlight successfully applied - send button highlighted');
            } else if (!sendButton) {
              // Send button not found yet, try again after a short delay (but only once)
              setTimeout(() => {
                const sendButton2 = document.querySelector('[data-lumi="chat-send"]');
                if (sendButton2 && !sendButton2.classList.contains('tour-highlight-active')) {
                  sendButton2.classList.add('tour-highlight-active');
                  
                  // 🔑 CRITICAL: Only set ref to true AFTER confirming classes were successfully applied
                  step3HighlightedRef.current = true;
                  console.log('[TourManager] Step', currentStepIndex + 1, 'highlight successfully applied (delayed) - send button highlighted');
                } else {
                  console.warn('[TourManager] Send button still not found after delay - highlight may not be applied');
                }
              }, 500);
            } else {
              // Send button already has highlight, confirm ref is set
              step3HighlightedRef.current = true;
              console.log('[TourManager] Step', currentStepIndex + 1, 'highlight already applied - send button already highlighted');
            }
          };
          
          // Wait for tooltip to be ready, then apply highlight with a coordinated delay
          let tooltipCheckAttempts = 0;
          const maxTooltipChecks = 30; // 3 seconds max wait for tooltip
          
          const checkTooltipReady = () => {
            tooltipCheckAttempts++;
            const tooltipReady = !!(window as any).__lumiStep3TooltipReady;
            // Check for react-joyride tooltip in DOM (various possible selectors)
            const tooltipVisible = !!(
              document.querySelector('[data-testid="react-joyride-tooltip"]') ||
              document.querySelector('.react-joyride__tooltip') ||
              document.querySelector('[role="dialog"][aria-live]')
            );
            
            if (tooltipReady || tooltipVisible) {
              // Tooltip is ready, apply highlight after a coordinated delay to ensure both appear together
              console.log('[TourManager] Tooltip confirmed ready, applying highlight in 400ms');
              setTimeout(applyHighlight, 400);
            } else if (tooltipCheckAttempts >= maxTooltipChecks) {
              // Timeout - apply highlight anyway to prevent indefinite waiting
              console.warn('[TourManager] Tooltip check timeout, applying highlight anyway');
              setTimeout(applyHighlight, 400);
            } else {
              // Tooltip not ready yet, check again
              step3HighlightIntervalRef.current = setTimeout(checkTooltipReady, 100);
            }
          };
          
          // Start checking for tooltip readiness
          checkTooltipReady();
        } else if (attempts >= maxAttempts) {
          console.warn('[TourManager] Timeout waiting for encodings panel to unfold, attempting highlight anyway');
          // Stop polling on timeout
          if (step3HighlightIntervalRef.current) {
            clearTimeout(step3HighlightIntervalRef.current);
            step3HighlightIntervalRef.current = null;
          }
          
          // Note: encodings-panel highlight is now handled by ChartBuilderCard via React prop
          // No DOM manipulation needed here
          
          // Try to highlight send button if it exists
          // Note: Send button click handler is attached in main effect, no need to attach here
          const sendButton = document.querySelector('[data-lumi="chat-send"]');
          if (sendButton && !sendButton.classList.contains('tour-highlight-active')) {
            sendButton.classList.add('tour-highlight-active');
            
            // 🔑 CRITICAL: Only set ref to true AFTER confirming classes were successfully applied
            step3HighlightedRef.current = true;
            console.log('[TourManager] Step', currentStepIndex + 1, 'highlight applied on timeout - send button highlighted');
          } else {
            // Elements not ready - don't set ref to true, allow retry on next render
            console.warn('[TourManager] Timeout reached but elements not ready - ref NOT set, will retry on next render');
            // Keep step3HighlightedRef.current = false to allow retry
          }
        } else {
          // Panel not ready yet, check again after a short delay
          step3HighlightIntervalRef.current = setTimeout(checkPanel, 200);
        }
      };
      
      // Start checking after initial delay to allow panel to render
      // Use a longer delay to ensure tooltip has time to appear first
      step3HighlightIntervalRef.current = setTimeout(checkPanel, 800);
    };

    // 🔑 Also handle Enter key (for users who never actually click the button)
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      // Narrow to the chat input; adjust selector if yours differs
      const inChatInput =
        !!target.closest('[data-lumi="chat-input"]') ||
        !!target.closest('[data-testid="chat-input"]') ||
        !!target.closest('[data-testid="input-chat"]');
      if (!inChatInput) return;
      onSend();
    };

    // 🔑 CRITICAL: Always attach send handler when on step 1 or 2, even if paused
    // This ensures the tooltip hides immediately when user sends
    const sendButton = document.querySelector('[data-lumi="chat-send"]');
    if (sendButton && !(sendButton as any).__tourClickHandler) {
      const clickHandler = () => {
        console.log('[TourManager] Send button clicked on step', currentStepIndex + 1, ', advancing to step', REVIEW_PLAN_INDEX + 1);
        onSend();
      };
      (sendButton as any).__tourClickHandler = clickHandler;
      sendButton.addEventListener('click', clickHandler, { once: false });
    }
    
    // 🔑 CRITICAL: Highlight send button when on step 1 or 2, even if tour is paused
    // The send button should always be highlighted on step 1 or 2 to guide the user
    if (sendButton && !sendButton.classList.contains('tour-highlight-active')) {
      sendButton.classList.add('tour-highlight-active');
      console.log('[TourManager] Step', currentStepIndex + 1, ': Send button highlighted');
    }
    
    // Only start full highlighting (encodings panel + coordinated timing) if isRunning is true
    if (shouldHighlight) {
      const encodingsPanel = document.querySelector('[data-lumi="encodings-panel"]');
      const alreadyHighlighted = encodingsPanel?.classList.contains('tour-highlight-active');
      
      if (!step3HighlightedRef.current && !alreadyHighlighted) {
        waitAndHighlight();
      } else if (alreadyHighlighted && !step3HighlightedRef.current) {
        // Highlight is already applied but ref wasn't set (e.g., from previous render)
        step3HighlightedRef.current = true;
      }
    }
    
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      // Clear any pending intervals
      if (step3HighlightIntervalRef.current) {
        clearTimeout(step3HighlightIntervalRef.current);
        step3HighlightIntervalRef.current = null;
      }
      document.removeEventListener('keydown', handleKeyDown);
      
      // Only remove send handler if leaving step 1 or 2
      // Check current step index in cleanup to determine if we're leaving step 1 or 2
      const currentStepInCleanup = onboardingSteps[currentStepIndexRef.current];
      const isStillOnStep1Or2 = currentStepInCleanup?.id === 'set-roles' || currentStepInCleanup?.id === 'choose-chart-type';
      if (!isStillOnStep1Or2) {
        const sendButton = document.querySelector('[data-lumi="chat-send"]');
        if (sendButton) {
          const clickHandler = (sendButton as any).__tourClickHandler;
          if (clickHandler) {
            sendButton.removeEventListener('click', clickHandler);
            delete (sendButton as any).__tourClickHandler;
          }
        }
      }
      // DON'T remove highlights in cleanup - let the next effect run decide
      // This prevents flickering when the effect re-runs due to other state changes
    };
  }, [isRunning, currentStepIndex, advanceFromEncodingsToReviewPlan, SET_ROLES_INDEX, CHOOSE_CHART_INDEX, onboardingSteps]);

  // Step 4: Highlight approve & run button when tour is running
  useEffect(() => {
    const currentStep = onboardingSteps[currentStepIndex];
    const isPlanStep = currentStep?.id === 'review-plan';
    const isOnStep4 = currentStepIndex === REVIEW_PLAN_INDEX;

    const approveButton = document.querySelector(
      '[data-lumi="btn-plan-recommended"]'
    ) as HTMLElement | null;

    // 🔑 CRITICAL: Only remove handler when actually leaving step 4, not when tooltip is closed
    // The approve button handler must stay attached even if tooltip is closed, so Step 5 can trigger
    const isLeavingStep4 = !isOnStep4;
    
    if (isLeavingStep4) {
      if (approveButton) {
        approveButton.classList.remove('tour-highlight-active');
        const clickHandler = (approveButton as any).__tourClickHandler;
        if (clickHandler) {
          approveButton.removeEventListener('click', clickHandler);
          delete (approveButton as any).__tourClickHandler;
        }
      }
      return;
    }

    // 🔑 CRITICAL: Always attach approve button handler when on step 4, even if tooltip is closed
    // This ensures Step 5 can trigger when plan is approved
    if (approveButton && !(approveButton as any).__tourClickHandler) {
      const clickHandler = () => {
        console.log('[TourManager] Approve button clicked on step 4, advancing to step 5');
        // Remove highlight…
        approveButton.classList.remove('tour-highlight-active');
        // …and advance tour logic: Step 4 → final step (save-and-actions)
        advanceFromPlanToFinalStep();
      };
      (approveButton as any).__tourClickHandler = clickHandler;
      approveButton.addEventListener('click', clickHandler, { once: false });
    }

    // Only highlight when tour is running
    if (isPlanStep && isRunning && approveButton) {
      approveButton.classList.add('tour-highlight-active');
    } else if (approveButton) {
      approveButton.classList.remove('tour-highlight-active');
    }

    return () => {
      // Only remove handler if leaving step 4
      // Check current step index in cleanup to determine if we're leaving step 4
      const currentStepInCleanup = onboardingSteps[currentStepIndexRef.current];
      const isStillOnStep4 = currentStepInCleanup?.id === 'review-plan';
      if (!isStillOnStep4) {
        const approveButtonInCleanup = document.querySelector('[data-lumi="btn-plan-recommended"]') as HTMLElement | null;
        if (approveButtonInCleanup) {
          approveButtonInCleanup.classList.remove('tour-highlight-active');
          const clickHandler = (approveButtonInCleanup as any).__tourClickHandler;
          if (clickHandler) {
            approveButtonInCleanup.removeEventListener('click', clickHandler);
            delete (approveButtonInCleanup as any).__tourClickHandler;
          }
        }
      }
    };
  }, [currentStepIndex, isRunning, advanceFromPlanToFinalStep, REVIEW_PLAN_INDEX, onboardingSteps]);

  // Handle post-save CTA callbacks
  const handlePostSaveCTACallback = useCallback((data: CallBackProps) => {
    const { action, index, status } = data;

    if (status === 'finished' || status === 'skipped') {
      setIsPostSaveMode(false);
      setIsRunning(false);
      return;
    }

    if (action === 'next') {
      const nextIndex = index + 1;
      if (nextIndex < postSaveCTASteps.length) {
        setCurrentStepIndex(nextIndex);
      } else {
        // All CTAs shown, restore main tour or finish
        setIsPostSaveMode(false);
        setIsRunning(false);
      }
    }

    if (action === 'close') {
      setIsPostSaveMode(false);
      setIsRunning(false);
    }
  }, []);

  const contextValue: TourContextValue = {
    startTour,
    resumeIfNeeded,
    dismiss,
    isActive: isRunning,
    currentStep: isRunning ? currentStepIndex : null,
    skip,
    onboardingStatus: serverOnboardingStatus,
  };

  // Expose tour state to window for chat component to check without causing re-renders
  useEffect(() => {
    try {
      (window as any).__lumiTourState = {
        isActive: isRunning,
        currentStep: isRunning ? currentStepIndex : null,
      };
    } catch {}
  }, [isRunning, currentStepIndex]);

  // Determine if current step should allow spotlight clicks
  const currentStep = onboardingSteps[currentStepIndex];
  const shouldAllowSpotlightClicks = currentStep?.id === 'choose-chart-type' || currentStep?.id === 'send-message';
  // Only disable overlay close for onboarding tour, not for post-save tours
  const shouldDisableOverlayClose = !isPostSaveMode;

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      <Suspense fallback={null}>
          <Joyride
          ref={joyrideRef}
          steps={currentSteps}
          run={isRunning}
          stepIndex={currentStepIndex}
          continuous={false}
          showProgress
          showSkipButton={false}
          callback={isPostSaveMode ? handlePostSaveCTACallback : handleJoyrideCallback}
          styles={joyrideStyles}
          disableOverlayClose={shouldDisableOverlayClose}
          disableCloseOnEsc={false}
          scrollToFirstStep={false}
          spotlightClicks={shouldAllowSpotlightClicks}
          disableScrolling={true}
          floaterProps={{
            disableAnimation: false,
            placement: 'bottom',
            styles: {
              floater: {
                filter: 'none',
              },
            },
          }}
        />
      </Suspense>
    </TourContext.Provider>
  );
}

/**
 * Hook to access tour context
 */
export function useTour(): TourContextValue {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within TourManagerProvider');
  }
  return context;
}

/**
 * Safe hook to access tour context (returns defaults if not available)
 * Use this when the component might be rendered outside TourManagerProvider
 */
export function useSafeTour(): TourContextValue {
  const context = useContext(TourContext);
  if (!context) {
    // Return default values if context is not available
    return {
      startTour: () => {},
      resumeIfNeeded: () => {},
      dismiss: () => {},
      isActive: false,
      currentStep: null,
      skip: () => {},
      onboardingStatus: 'loading',
    };
  }
  return context;
}
